/**
 * English mirror of `manualContent.js`. Same shape, every text field
 * translated. The French version remains the source of truth for new
 * content; this file is updated in lockstep.
 *
 * 31/05/2026 — Full EN translation for the pedagogical manual (Phase 3).
 *
 * NOTE: French legal/fiscal terms (URSSAF, CFE, EURL, SARL, SAS, BIC,
 * AGS, DREAL, TGI, IS, TVA, AGE, etc.) are kept as-is with parenthetical
 * English explanations, because the game is built around the French
 * fiscal system — translating these to UK/US equivalents would break
 * accuracy.
 */
export const MANUAL_CHAPTERS_EN = [
  // ════════════════════════════════════════════════════════════════════
  // 1. TAXATION & LEGAL STATUSES
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'fiscalite',
    title: 'Taxation & Legal statuses',
    subtitle: 'VAT, CIT, progressive corporate structures',
    icon: 'Scale',
    color: '#7C3AED',
    pages: [
      {
        type: 'cover',
        title: 'Taxation & Legal statuses',
        intro: "Taxation shapes your company's life from the very first euro of revenue. Knowing the thresholds and obligations lets you anticipate rather than suffer.",
        bullets: [
          'Sole proprietor → EURL → SARL → SAS depending on revenue',
          'VAT regimes: exemption, simplified, standard',
          'Self-employed social charges vs. salaried',
        ],
      },
      {
        type: 'definitions',
        title: 'Key definitions',
        items: [
          { term: 'Sole proprietor (Auto-entrepreneur)', def: 'Simplified individual status. VAT exemption up to €91,900 in revenue. Flat-rate social charges (~22% of revenue).' },
          { term: 'EURL', def: 'Single-shareholder limited liability company. Business / personal assets are separated. Self-employed manager (TNS), charges ~45% of compensation.' },
          { term: 'SARL', def: 'Limited liability company. Minimum 2 partners. Liability limited to contributions. Flexible, well suited to family-owned SMEs.' },
          { term: 'SAS', def: 'Simplified joint-stock company. The president has employee-equivalent social charges (~75%). Highly flexible articles, ideal for raising capital.' },
          { term: 'Output VAT', def: 'The tax you charge your customers (20% in France for intra-EU transport).' },
          { term: 'Input VAT', def: 'The tax you pay on your purchases (fuel, vehicles, supplies) and reclaim from the Treasury.' },
          { term: 'Simplified VAT regime', def: 'Semi-annual VAT return. Available up to €840,000 in revenue before tax.' },
          { term: 'Standard VAT regime', def: 'Monthly VAT return. Mandatory above €840,000 in revenue before tax.' },
          { term: 'Automatic tax provisioning (Midal perk)', def: "Mechanism that ring-fences, every single day, the net output VAT and 25% of the day's profit into two dedicated reserves (VAT, CIT). Available only after hiring Tristan Midal (CFO). Any over-provisioning is refunded into the treasury at year-end." },
          { term: '25% fiscal reserve (Livret A)', def: 'Optional feature: at each month-end close, 25% of net profit is transferred to a dedicated Livret A pro account (3% APY, interest capitalised daily). Automatically released at year-end (tops up CIT if short, otherwise returns straight to treasury).' },
        ],
      },
      {
        type: 'formulas',
        title: 'Calculation formulas',
        items: [
          {
            name: 'VAT due',
            formula: 'VAT due = Output VAT − Input VAT',
            example: 'You invoice €50,000 ex-VAT (€10,000 VAT), spend €8,000 ex-VAT on fuel (€1,600 VAT). VAT due = 10,000 − 1,600 = €8,400.',
          },
          {
            name: 'Ex-VAT / Incl-VAT conversion',
            formula: 'Incl-VAT price = Ex-VAT × (1 + VAT rate)\nEx-VAT price = Incl-VAT ÷ (1 + VAT rate)',
            example: 'Contract listed at €12,000 incl-VAT at 20% → ex-VAT = 12,000 / 1.20 = €10,000.',
          },
          {
            name: 'TNS social charges (majority manager)',
            formula: 'TNS charges ≈ 45% of net compensation',
            example: 'You pay yourself €3,000/month net → ~€1,350 in extra URSSAF charges.',
          },
          {
            name: 'Corporate income tax (CIT) — simplified rate',
            formula: 'CIT = 15% × profit up to €42,500 + 25% above',
            example: '€60,000 profit → CIT = 15% × 42,500 + 25% × 17,500 = 6,375 + 4,375 = €10,750.',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Glossary',
        items: [
          { word: 'BIC', def: 'Industrial and Commercial Profits — the fiscal category for road transport.' },
          { word: 'Share capital', def: 'Initial contribution of the partners. Legal minimum for SARL/SAS: €1. In practice: aim for ≥ €5,000 for banking credibility.' },
          { word: 'Dividend', def: 'Share of profit distributed to partners. Subject to flat tax (30%) or progressive scale.' },
          { word: 'Fiscal year', def: 'The 12-month period for which the result is calculated. Often aligned with the calendar year.' },
          { word: 'Tax bundle (liasse fiscale)', def: 'Annual filing sent to the tax office summarising the balance sheet, income statement and appendices.' },
          { word: 'URSSAF', def: 'French agency collecting social security contributions (health, retirement, family allowances).' },
        ],
      },
      {
        type: 'diagram',
        title: 'Choosing your legal status',
        intro: 'The progression of statuses follows revenue. Anticipate the change to avoid an abrupt fiscal shift.',
        diagram: 'pyramid-statuts',
        legend: [
          { label: 'Sole proprietor', desc: 'Easy start, but low cap and no reclaimable VAT.' },
          { label: 'EURL',             desc: 'Protected personal assets, TNS social charges (~45%).' },
          { label: 'SARL',             desc: 'Multiple partners, flexible governance, proven framework.' },
          { label: 'SAS',              desc: 'Ideal for raising capital, president has employee-equivalent charges (~75%).' },
        ],
      },
      {
        type: 'cases',
        title: 'Case studies',
        items: [
          {
            title: 'Crossing the VAT exemption threshold',
            situation: "You are a sole proprietor. Your trailing 12-month revenue reaches €89,000 and you forecast €8,000 this month.",
            solution: 'You will cross €91,900 — you exit the exemption. From day 1 of the month of overshoot, you must charge 20% VAT. Anticipate: adjust your ex-VAT pricing, update invoice templates, and file your first VAT return next month.',
            lesson: 'In-game, watch the "trailing 12-month revenue" KPI on the Taxation page. Moving into VAT cuts apparent profitability by ~17%.',
          },
          {
            title: 'EURL vs. SASU at €100k revenue',
            situation: 'You generate €100k/year. You hesitate between EURL (TNS manager, ~45% charges) and SASU (employee-equivalent president, ~75% charges).',
            solution: 'For an equal compensation amount, EURL is ~30% cheaper in social charges. SASU is nonetheless preferable if you plan to pay yourself dividends (30% flat tax, no social charges) or open up the share capital later.',
            lesson: 'Choose based on your exit strategy: EURL to optimise current pay, SASU for dividend taxation and flexibility.',
          },
          {
            title: 'Simplified regime → standard regime',
            situation: 'Your ex-VAT revenue just crossed €840,000. Your accountant calls you.',
            solution: 'You switch to standard regime: MONTHLY VAT returns (instead of semi-annual). Set up a monthly close process from next month, or face delays and URSSAF penalties.',
            lesson: 'Moving to the standard regime does not change the tax owed — it changes the frequency and the rigour of the filing. Late filing = 0.4%/month penalty.',
          },
          {
            title: 'Automatic VAT + CIT provisioning (Midal perk)',
            situation: 'You have just hired Tristan Midal (CFO) and toggled "Automatic tax provisioning" ON in Taxation. Daily profit: ~€600. Net output VAT: ~€120/day.',
            solution: "Every working day, the engine automatically debits from your treasury: €120 → VAT reserve + 25% × €600 = €150 → CIT reserve. Your displayed treasury now matches your true available cash. At VAT filing / CIT instalments, the reserve pays first. At year-end, any over-provisioning (if actual CIT is < 25%) is refunded as a bonus into treasury.",
            lesson: 'The Midal toggle eliminates the "big year-end CIT cheque" bad surprise. Cost: zero — it is just an accounting ring-fence. Activate it AS SOON as Midal is hired, unless you actively run the discipline yourself.',
          },
          {
            title: '25% fiscal reserve on Livret A (3% APY)',
            situation: 'You enable the "Fiscal reserve" option (Taxation → Overview). Net monthly profit: €8,000.',
            solution: 'At each month-end close, 25% of the net profit (= €2,000) is automatically transferred to a dedicated Livret A account, earning 3% APY (daily capitalised interest). At year-end, the whole balance is released: covers CIT if short, otherwise returned directly to treasury + accrued interest (~€30-60 per year for a €24k average reserve).',
            lesson: 'Stackable with the Midal toggle: Midal provisions at 1/365 per day, the Livret A reserve accumulates 25% of monthly result. Both together = a true fiscal safety net + small interest gain. Disable if you need cash to invest right now.',
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pitfalls to avoid',
        items: [
          {
            title: 'Confusing output VAT with treasury',
            mistake: 'You see €10,000 of output VAT on your account and use it to pay a salary.',
            signal: 'Treasury balance ≥ VAT due, shown in orange on the Taxation dashboard.',
            fix: 'VAT is NOT your money — you collect it for the State. Ring-fence it monthly to a separate account or sub-ledger "VAT to remit".',
          },
          {
            title: 'Staying sole proprietor above €92k revenue',
            mistake: 'You cross the cap but keep invoicing without VAT to avoid the paperwork.',
            signal: 'Pink notification "VAT exemption cap exceeded" + fiscal fine if audited.',
            fix: 'Switch to EURL/SASU as soon as forecast 12-month revenue reaches €85k. You gain input VAT recovery (fuel, vehicles) which more than offsets the new charges.',
          },
          {
            title: 'Forgetting the CFE',
            mistake: 'You pay CIT and VAT but ignore the CFE (Cotisation Foncière des Entreprises, local business tax), due every 15 December.',
            signal: 'Red notification "CFE unpaid" mid-December + 10% surcharge.',
            fix: 'CFE is due from the 2nd year of activity, even without an office (based on the depot rental value). Expect ~€1,500-3,000/year for a transport SME.',
          },
          {
            title: 'Disabling Midal provisioning without a safety net',
            mistake: 'You hire Midal then toggle off "Automatic tax provisioning" to free up apparent cash. Three months later, the Q2 CIT instalment hits and treasury is insufficient.',
            signal: 'Red notification "Insufficient funds for CIT Q2 instalment" + bank fiscal credit offered at 4.5% annual + a "rant" message from Midal in the Inbox at year-end.',
            fix: 'If you disable Midal, AT LEAST enable the 25% Fiscal reserve (Livret A) for a safety net. Ideal: leave both ON and forget the topic — Midal is here to optimise for you.',
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 2. FINANCE & ACCOUNTING
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'finance',
    title: 'Finance & Accounting',
    subtitle: 'Balance sheet, margin, EBITDA, runway',
    icon: 'TrendingUp',
    color: '#2563EB',
    pages: [
      {
        type: 'cover',
        title: 'Finance & Accounting',
        intro: 'Reading a balance sheet in 30 seconds, computing your break-even point, forecasting your cash flow: these skills separate an SME that lasts from one that goes under.',
        bullets: [
          'Income statement vs. balance sheet',
          'Gross margin vs. net margin',
          'Equity and solvency',
        ],
      },
      {
        type: 'definitions',
        title: 'Key definitions',
        items: [
          { term: 'Revenue (CA)', def: 'Sum of invoices issued (ex-VAT) over a period. Volume indicator, not profitability.' },
          { term: 'Gross margin', def: 'Revenue − direct cost of sales (fuel, tolls, driver wages). Measures operational profitability.' },
          { term: 'Net margin', def: 'Net profit ÷ revenue. Includes all charges (fixed, financial, fiscal). For road transport: 4-10% is normal.' },
          { term: 'EBITDA (EBE)', def: "Company's ability to generate cash before taxes and depreciation. EBITDA = Margin − external charges − wages." },
          { term: 'Equity', def: 'Contributions + reserves + result. If negative → red alert, banks refuse credits.' },
          { term: 'Assets', def: 'What the company owns (cash, receivables, vehicles). Always = Liabilities.' },
          { term: 'Liabilities', def: 'What the company owes (equity + debts + suppliers).' },
          { term: 'Runway', def: 'Number of months of remaining cash at current spending pace. If < 3 months → urgent corrective action.' },
          { term: 'Straight-line depreciation', def: "Default accounting mode: an asset's value (truck, office, software) is spread evenly over its useful life. E.g. €60,000 truck over 5 years = €12,000/year of depreciation charge." },
          { term: 'Accelerated (declining) depreciation', def: 'Optional mode allowed on new vehicles and certain assets: faster amortisation in early years (×1.75 of the straight-line). Advantage: immediate CIT savings. Disadvantage: lower depreciation in later years.' },
          { term: 'Net book value (NBV)', def: 'Asset value on the balance sheet after cumulative depreciation. NBV = Purchase price − cumulative depreciation. Tracked month-by-month in HST on the NBV evolution chart.' },
          { term: 'CFE (local business tax)', def: 'Local tax due from year 2 of activity. Calculated on the rental value of premises (depot + office) × municipal multiplier (0.8 to 1.6 depending on city). Typically €1,500-3,000/year for a transport SME. Due mid-December.' },
          { term: 'Regional aids (rents)', def: 'Recurring regional subsidy on the depot or office rent, unlocked based on the chosen location. Covers up to 30% of the monthly rent. Visible in the Finance tab and when buying/renting a location.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Calculation formulas',
        items: [
          {
            name: 'Net margin',
            formula: 'Net margin (%) = (Net profit ÷ Revenue) × 100',
            example: 'Revenue €600,000 / Net profit €42,000 → Margin = 7%.',
          },
          {
            name: 'EBITDA',
            formula: 'EBITDA = Revenue − Purchases − External services − Wages & charges',
            example: 'Revenue 50k − Fuel 12k − Subcontracting 3k − Wages 22k = EBITDA €13,000.',
          },
          {
            name: 'Cash flow capacity (CAF)',
            formula: 'CAF = Net result + Depreciation charges',
            example: 'Result €30,000 + Vehicle depreciation €18,000 = CAF €48,000.',
          },
          {
            name: 'Solvency ratio',
            formula: 'Solvency (%) = (Equity ÷ Total assets) × 100',
            example: 'Assets 500k / Equity 150k → 30% (healthy ≥ 25%).',
          },
          {
            name: 'Runway (months)',
            formula: 'Runway = Treasury ÷ Average monthly charges',
            example: 'Treasury €80,000 / Charges €25,000/month → 3.2 months (alert zone).',
          },
          {
            name: 'Annual straight-line depreciation',
            formula: 'Charge = Purchase price ÷ Useful life\n(standard: vehicle = 5 years, office = 8 years, software = 3 years)',
            example: 'Truck €60,000 over 5 years → €12,000/year, €1,000/month.',
          },
          {
            name: 'Declining (accelerated) depreciation',
            formula: 'Charge N = Opening NBV × (1/life) × 1.75 (vehicle coefficient)',
            example: 'Truck €60,000, 5 years, coef 1.75: Year 1 = 60,000 × 0.20 × 1.75 = €21,000. Vs. straight-line = €12,000. Immediate CIT saving.',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Glossary',
        items: [
          { word: 'Depreciation', def: "Accounting spread of a vehicle's cost over its useful life (5-7 years for a truck)." },
          { word: 'WCR (BFR)', def: 'Working Capital Requirement. Cash tied up in the operating cycle (receivables − payables).' },
          { word: 'Balance sheet', def: 'Snapshot of net worth at a given date. Assets = Liabilities.' },
          { word: 'Income statement', def: 'Flow of revenues and expenses over a period. Profit = Revenues − Expenses.' },
          { word: 'Charge', def: 'Non-cash expense (depreciation, provision) that reduces the result without touching treasury.' },
          { word: 'Provision', def: 'Anticipated charge for a probable risk (litigation, doubtful receivable).' },
          { word: 'Treasury', def: 'Cash immediately available on current accounts.' },
        ],
      },
      {
        type: 'diagram',
        title: 'The cash cycle',
        intro: 'Between the moment you invoice and the moment you collect, you have already paid fuel and wages. That is working capital requirement (WCR).',
        diagram: 'tresorerie-cycle',
        legend: [
          { label: 'Customer invoice', desc: 'Issued on delivery — legal cap 60 days.' },
          { label: 'Collection',        desc: 'Cash in 30 to 60 days later — meanwhile you pay everything.' },
          { label: 'Driver payroll',    desc: 'Monthly cash outflow, fixed and unavoidable.' },
          { label: 'Fuel',              desc: 'Near-daily outflow. Top variable cost item.' },
        ],
      },
      {
        type: 'cases',
        title: 'Case studies',
        items: [
          {
            title: 'Computing the break-even point',
            situation: 'You have €18,000/month of fixed charges (depot rent, fixed wages, loan instalments). Your average gross margin per contract is 35%.',
            solution: 'Monthly break-even = Fixed charges ÷ gross margin rate = 18,000 ÷ 0.35 = €51,430 in revenue. Below that, you lose money.',
            lesson: 'The break-even point tells you the MINIMUM revenue target to avoid sinking. Display it on the Dashboard and compare weekly.',
          },
          {
            title: 'Deciding on a 3rd truck',
            situation: 'Your 2 trucks run at 95% occupancy. A 3rd truck would cost €1,800/month in instalments + €1,200 in fixed charges. You estimate +€12,000/month in potential revenue at 40% margin.',
            solution: 'Expected net gain = 12,000 × 0.40 − 3,000 = €1,800/month. Decision: YES, the investment generates a positive surplus. Check runway before signing: you need at least 3 months of treasury to absorb the ramp-up.',
            lesson: 'Buy when occupancy exceeds 85% over 30 consecutive days. Sell when it drops below 60% over 60 days.',
          },
          {
            title: 'Spotting a drift before the crisis',
            situation: 'Your runway has gone from 6 months to 2.5 months over 3 consecutive months.',
            solution: 'Immediate action: (1) freeze hiring, (2) renegotiate with the bank (partial deferral), (3) sell an under-used vehicle. DO NOT: take a new loan to plug current charges — that is the spiral.',
            lesson: 'Runway is the vital KPI. Track it monthly. Downward slope for 3 months in a row = red alert, even if everything else looks OK.',
          },
          {
            title: 'Activating regional aids (rent + CFE)',
            situation: 'You hire 5 net CDI drivers over the year and invest €60k in CAPEX (3rd depot + expansion). You have 3 support roles hired by 31 December.',
            solution: 'On 1 January, the Regional Council automatically checks your thresholds and activates 3 aids: (a) HQ rent waived for 12 months (≈€14,400/year saved), (b) office rent waived + one-shot €5,000 FEDER grant, (c) CFE −15% for 12 months. Annual total: ~€20,000 of charges wiped. No manual filing — paid/deducted automatically.',
            lesson: 'Watch the Finance → Rents & Aids tab to see the thresholds in real time. Regional aids feed into your forecast: do not launch a major hire in March if you can defer it to November to cross the threshold of year N+1.',
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pitfalls to avoid',
        items: [
          {
            title: 'Confusing profit with treasury',
            mistake: 'The income statement shows +€12,000/month of profit. You pay a dividend. Treasury turns negative the week after.',
            signal: 'Gap > 30% between cumulative net profit and actual treasury = poorly managed WCR.',
            fix: 'Before any dividend, check CAF (cash flow capacity) AND the 90-day treasury projection. Profit ≠ available cash.',
          },
          {
            title: 'Ignoring customer payment terms',
            mistake: 'You accept all contracts without checking payment terms. The big account pays at 60 days.',
            signal: '"Receivables" balance sheet line that swells month over month.',
            fix: 'Negotiate 30% deposit on signature for contracts > €5,000. Refuse terms > 60 days unless credit insurance is in place.',
          },
          {
            title: 'Underestimating depreciation',
            mistake: 'You look at gross margin (high, few direct charges) and forget that your trucks lose €12,000/year of value.',
            signal: 'Gross margin > 40% but net result < 5% = heavy depreciation ignored in the decision.',
            fix: 'Compute your cost/km INCLUDING vehicle depreciation (approx. €0.12-0.18/km). Otherwise your contract prices fail to cover fleet renewal.',
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 3. BANKING & CREDIT
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'banque',
    title: 'Banking & Credit',
    subtitle: 'Rates, instalments, mortgages, overdraft fees',
    icon: 'Landmark',
    color: '#059669',
    pages: [
      {
        type: 'cover',
        title: 'Banking & Credit',
        intro: 'Banking leverage accelerates your growth — or buries your SME, depending on how you use it. Master the total cost of a loan before signing.',
        bullets: [
          'How an amortising loan works',
          'True cost: nominal rate vs. APR',
          'Collateral: guarantee, mortgage, pledge',
        ],
      },
      {
        type: 'definitions',
        title: 'Key definitions',
        items: [
          { term: 'Principal', def: 'Sum lent by the bank at inception. Repaid through instalments.' },
          { term: 'Nominal rate', def: 'Annual rate advertised by the bank, excluding fees.' },
          { term: 'APR (TAEG)', def: 'Annual Percentage Rate. Includes interest + arrangement fees + insurance. This is the REAL cost of credit.' },
          { term: 'Instalment', def: 'Fixed monthly payment = principal portion + interest portion.' },
          { term: 'Amortisation', def: 'Principal portion repaid each month. Grows gradually over the life of the loan.' },
          { term: 'Interest', def: 'The bank\'s remuneration. Decreases gradually (computed on remaining principal).' },
          { term: 'Overdraft fees (agios)', def: 'Fees charged for unauthorised overdraft or exceeding the limit. Typically 16-20% annual — absolutely to avoid.' },
          { term: 'Mortgage', def: 'Security on a real estate asset. The bank can seize it in case of default.' },
          { term: 'Credit score (HST)', def: 'Internal score computed by each bank (300-850) from your cash, debt ratio, seniority, payment regularity. Drives the offered rate: excellent (>720) → preferential rate, average (600-720) → standard rate, low (<600) → loan refused.' },
          { term: 'Borrowing cap per bank', def: 'Each bank applies its own "max debt / monthly revenue" ratio (3.5 to 4.2 depending on the bank) with a revenue floor that scales with company age — about €4,000/month at D1, €8,000/month at D80+. Spreading across 2-3 banks multiplies the total capacity.' },
          { term: 'Multi-banking', def: 'Strategy of spreading debt across multiple banks. Reduces dependency risk, improves negotiated terms and enables higher cumulative borrowing.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Calculation formulas',
        items: [
          {
            name: 'Loan instalment (classic method)',
            formula: 'M = C × [t × (1+t)^n] / [(1+t)^n − 1]\nwhere t = monthly rate, n = number of instalments, C = principal',
            example: 'Principal €60,000, rate 4% annual over 5 years → instalment ≈ €1,105.',
          },
          {
            name: 'Total cost of credit',
            formula: 'Cost = (Instalment × Number of instalments) − Principal',
            example: '€60,000 over 5 years at 4% → 1,105 × 60 − 60,000 = €6,300 of cumulative interest.',
          },
          {
            name: 'Monthly interest',
            formula: 'Monthly interest = Remaining principal × (annual rate ÷ 12)',
            example: 'Remaining principal €55,000, rate 4% → 55,000 × 0.04 / 12 = €183 of interest this month.',
          },
          {
            name: 'Debt ratio',
            formula: 'Debt ratio (%) = (Total instalments ÷ Monthly revenue) × 100',
            example: 'Instalments €4,500, revenue €25,000 → 18% (healthy zone).',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Glossary',
        items: [
          { word: 'Guarantor', def: 'Person or company who agrees to repay if the borrower defaults.' },
          { word: 'Lease (crédit-bail)', def: 'Long-term rental of a vehicle with a final purchase option. Accounting-wise more flexible than a loan.' },
          { word: 'Authorised overdraft', def: 'Negative balance tolerated by the bank within a contractual limit. Beyond: increased fees.' },
          { word: 'Repayment deferral', def: 'Initial period where you only pay interest. Eases cash flow at start-up.' },
          { word: 'Due date', def: 'Date by which an instalment must be paid.' },
          { word: 'Arrangement fee', def: 'Flat fee on signature (often 1% of principal). Negotiable.' },
          { word: 'Pledge', def: 'Security on a movable asset (vehicle). The bank can recover it if unpaid.' },
          { word: 'Early repayment penalty (IRA)', def: "Penalty if you repay before term. Capped at 6 months' interest." },
        ],
      },
      {
        type: 'diagram',
        title: 'Amortisation of a loan',
        intro: 'In the early years, you pay mostly interest. As time passes, the principal portion grows. Understanding this curve helps you better negotiate duration.',
        diagram: 'credit-amortissement',
        legend: [
          { label: 'Principal', desc: 'Portion of the instalment that actually repays the debt.' },
          { label: 'Interest',  desc: 'Cost paid to the bank. Decreases over time (computed on remaining balance).', color: '#DC2626' },
        ],
      },
      {
        type: 'cases',
        title: 'Case studies',
        items: [
          {
            title: '4 years or 7 years for a truck?',
            situation: 'Truck €80,000 at 4% interest. 4-year instalment = €1,808 (total cost €6,800). 7-year instalment = €1,094 (total cost €11,900).',
            solution: '7 years = €700 of instalment saved each month (+€8,400/year of cash). 4 years = €5,100 of interest saved. Choose by priority: cash (7 years) or cost (4 years).',
            lesson: 'For a truck meant to work 5+ years, align the loan amortisation with the asset\'s real useful life. Cash > marginal interest.',
          },
          {
            title: 'Anticipating an overdraft fee',
            situation: 'Your balance falls to -€8,000 on Thursday. Your authorised overdraft is €5,000. Customer collection expected next Tuesday (4 days later).',
            solution: 'Fees = 8,000 × 18%/year × 4/365 = ~€16. But €3,000 above the authorised overdraft → flat €35 penalty + increased fees. Total cost ~€70. Solution: call the bank for a one-off authorised excess (often free).',
            lesson: 'A phone call to your bank advisor costs less than overdraft fees. Anticipate each time a major collection slips.',
          },
          {
            title: 'Opportunistic refinancing',
            situation: 'You have a 5.5% loan with 3 years remaining (remaining principal €30,000). Market rates are at 3.2%.',
            solution: 'Gross saving = (5.5% − 3.2%) × 30k × 3 = €2,070. Max early repayment penalty = 6 months interest at 5.5% = ~€825. New arrangement fees ~€300. Net saving ≈ €950. Decision: YES, negotiate firmly.',
            lesson: 'Refinance when rates drop by > 1.5 points. Always do the net calculation after early repayment penalty + fees.',
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pitfalls to avoid',
        items: [
          {
            title: 'Comparing on nominal rate instead of APR',
            mistake: 'Bank A offers 3.5% nominal + 1% arrangement fees. Bank B offers 3.8% nominal with no fees. You pick A as "cheaper".',
            signal: 'APR gap > 0.5 points despite a lower nominal rate = fees or insurance inflate the real cost.',
            fix: 'Compare ONLY the APR. It is the only rate that integrates everything (interest + fees + insurance).',
          },
          {
            title: 'Stacking loans without capping debt',
            mistake: 'You take a 3rd vehicle loan. Your total instalments reach 38% of monthly revenue.',
            signal: '"Debt ratio" indicator > 30% on the Finance dashboard.',
            fix: 'Prudent cap: 25% of monthly revenue in cumulative instalments. Above 30%, refuse any new loan even if the bank approves it.',
          },
          {
            title: 'Mortgaging the depot for short-term needs',
            mistake: 'You mortgage your warehouse to finance 6 months of fuel. The bank will seize it on default.',
            signal: 'You pledge a REAL asset for an OPERATIONAL debt = signal of structural over-indebtedness.',
            fix: 'Mortgages are for LONG investments (real estate, durable equipment). For cash, negotiate a short-term loan or factoring (Dailly).',
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 4. HR & SOCIAL CLIMATE
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'rh',
    title: 'HR & Social climate',
    subtitle: 'Satisfaction, burnout, competence',
    icon: 'Users',
    color: '#D97706',
    pages: [
      {
        type: 'cover',
        title: 'HR & Social climate',
        intro: 'A driver who quits costs €8,000 in hidden costs (recruitment, training, productivity loss). Investing in social climate = direct ROI on performance.',
        bullets: [
          'Satisfaction: the engine of loyalty',
          'Burnout: an early signal to detect',
          'Competence vs. seniority',
        ],
      },
      {
        type: 'definitions',
        title: 'Key definitions',
        items: [
          { term: 'Satisfaction (0-100)', def: "Driver wellbeing score. < 40: likely to quit. > 70: maximum performance." },
          { term: 'Competence (0-100)', def: 'Driver technical level. Grows with missions and training. Direct impact on delivery quality.' },
          { term: 'Burnout', def: 'Professional exhaustion. Auto-detected from a combination of low satisfaction + intense missions.' },
          { term: 'Employer cost', def: "Gross salary + employer charges (~42%) + meal vouchers + insurance. The actual cost of the driver to the company." },
          { term: 'Salary renegotiation', def: 'When a senior driver asks for a raise. Refusing = satisfaction drops. Accepting = +15% cost.' },
          { term: 'Specialties', def: 'Specific skills (refrigerated, long distance, ADR, urban). Unlock access to certain premium contracts.' },
          { term: 'XP / Level', def: 'Cumulative experience. Junior → Confirmed → Senior. Senior = +25% productivity, but higher salary demands.' },
          { term: 'Licences (B / C1 / C / CE)', def: 'HGV licence category held by the driver. Determines which vehicles can be driven: B = van < 3.5 t (T1), C = solo truck > 7.5 t (T2), CE = HGV + trailer (T3, T4 and above). Hierarchical: a CE driver can drive everything.' },
          { term: 'Licence validation', def: 'Hard business rule: the engine refuses to assign a vehicle if the driver does not have the required licence. The yellow banner shows the missing licence to guide the player (train the driver or hire someone else).' },
          // ── 02/06/2026 — French legal HR obligations ──
          { term: 'CDI / CDD', def: 'CDI = open-ended contract, the standard (95% of transport). CDD = fixed-term contract, mandatorily written, requires a specific reason (replacement, activity peak). At CDD end: 10% precarity bonus on gross paid.' },
          { term: 'Probation period', def: 'Trial phase at the start of a CDI: 2 months for employees (renewable once). Free termination on both sides, no statutory indemnity, but a prevention period proportional to seniority (24h to 1 month). Beyond 8 months total, termination becomes a classic separation.' },
          { term: 'Mutual termination (rupture conventionnelle)', def: 'Amicable termination of a CDI, negotiated between employer and employee and APPROVED by the DREETS. No cause to justify, no notice period, but a **specific severance indemnity (ISRC)** at least equal to the statutory dismissal indemnity. The recommended mode in-game: no prud\'hommes risk.' },
          { term: 'Dismissal (real & serious cause)', def: 'Employer-initiated termination of a CDI, justified (economic, fault, unfitness). Strict procedure: summons → preliminary interview → notification. 2-month notice period for managers. Statutory indemnity identical to mutual termination. Risk of labour-court challenge if the cause is judged insufficient.' },
          { term: 'ISRC — Specific Mutual Termination Indemnity', def: 'Statutory floor: ¼ month gross salary × seniority for the first 10 years, then ⅓ month × year beyond. Exempt from social contributions up to 2 × PASS (~€94,000), but subject to CSG/CRDS and social flat charge (~30% on excess).' },
          { term: 'FCO — Mandatory Continuous Training', def: '35 hours to renew every **5 years** for every professional driver > 3.5 t. Without a valid FCO, the driver CANNOT legally drive. DREAL roadside fine if checked: €800 to €1,500 + possible vehicle immobilisation.' },
          { term: 'Medical fitness exam', def: 'Mandatory for every HGV driver, to renew every **5 years** before age 60, then every **2 years**. Performed by a prefecture-approved doctor. Without a valid exam, the professional licence is suspended.' },
          { term: 'ADR — European Agreement', def: 'Specialised certification for dangerous goods transport (chemistry, fuels, batteries). 5 initial days + refresher every 5 years. Without a valid certificate, dangerous goods transport is forbidden.' },
          { term: 'CACES R489 / R372', def: 'Certificate of Aptitude for Safe Conduct (forklift, crane, lift). Refresher **every 5 years**. Essential for warehouse handling or BTP construction sites.' },
          { term: 'DUERP — Single Risk Assessment', def: 'Single Risk Assessment Document. Mandatory from the 1st employee, annual update. Tracks identified risks per role (handling, driving, loading).' },
          { term: 'Notice period', def: 'Mandatory period between termination announcement and end of contract. Resignation: 1 month (worker) / 2 months (manager). Dismissal: 1 month (< 2 years seniority) / 2 months (≥ 2 years). Mutual termination: **no notice period** — one of its key advantages.' },
          { term: 'Employer social contributions', def: 'Social charges paid by the employer on the gross salary: URSSAF (health, retirement, unemployment, family) ≈ 42%. That\'s why `Employer cost ≈ Gross salary × 1.42`.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Calculation formulas',
        items: [
          {
            name: 'Monthly employer cost',
            formula: 'Cost = Gross salary × (1 + 0.42) + monthly benefits',
            example: 'Gross salary €2,200 + €50 meal vouchers + €30 insurance = 2,200 × 1.42 + 80 = €3,204.',
          },
          {
            name: 'Replacement cost (hidden cost)',
            formula: 'Replacement ≈ 3 months of loaded salary + productivity loss',
            example: 'Employer cost €3,200/month × 3 = €9,600 (visible) + €2,000 (training/loss) = ~€11,600.',
          },
          {
            name: 'Driver performance score',
            formula: 'Score = competence + xp×0.001 + trips×0.05 + satisfaction×0.02',
            example: 'Competence 75 + 1,500 XP + 80 trips + 85 sat = 75 + 1.5 + 4 + 1.7 = 82.2.',
          },
          {
            name: 'Real productivity',
            formula: 'Productivity = (competence ÷ 100) × (satisfaction ÷ 100) × 100%',
            example: 'Competence 80 × Satisfaction 60 = 0.80 × 0.60 = 48% effective productivity.',
          },
          // ── 02/06/2026 — Legal obligations: indemnity calculation ──
          {
            name: 'ISRC indemnity (mutual termination / lawful dismissal)',
            formula: 'ISRC = (¼ × monthly gross × seniority[0-10 yrs]) + (⅓ × monthly gross × seniority[> 10 yrs])',
            example: 'Gross €3,000, 5 years seniority → 0.25 × 3,000 × 5 = €3,750. For 15 years → 0.25 × 3,000 × 10 + (1/3) × 3,000 × 5 = €7,500 + €5,000 = €12,500.',
          },
          {
            name: 'Total employer cost of a termination',
            formula: 'Cost = ISRC + Notice (if dismissal) + Paid-leave balance + Charges on indemnities',
            example: 'Mutual term. 5 years, gross €3,000: ISRC €3,750 + Notice €0 + PL ~€300 + Charges 12% ≈ €450 + 42% on PL balance ≈ €125 → **~€4,625**. The same as a dismissal with 2-month notice: ~€12,000 (~€8,500 more).',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Glossary',
        items: [
          { word: 'AGS', def: 'French wage guarantee scheme. Advances unpaid wages in case of liquidation.' },
          { word: 'Driver/vehicle compatibility', def: 'Affinity between a driver and a truck type. Boosts satisfaction (+5) when well matched.' },
          { word: 'Collective agreement (CCN)', def: 'Industry-specific rules complementing the Labour Code. For transport: CCN 1979.' },
          { word: 'Resignation', def: 'Termination by the employee. No severance, 1-month notice.' },
          { word: 'Severance pay', def: 'One quarter month per year of seniority up to 10 years, one third beyond. Paid on non-gross-misconduct dismissal.' },
          { word: 'Personality trait', def: 'Behavioural particularity (perfectionist, sociable, independent). Affects satisfaction depending on context.' },
        ],
      },
      {
        type: 'diagram',
        title: 'Satisfaction → resignation',
        intro: "Satisfaction is non-linear: below 40, the driver actively looks elsewhere. The slope steepens abruptly.",
        diagram: 'satisfaction-curve',
        legend: [
          { label: 'Productive (60-100)', desc: 'The driver delivers, accepts overtime, recommends the company.', color: '#10B981' },
          { label: 'Alert (40-60)',       desc: 'Productivity drops, delays, conflicts — the warning is here.', color: '#F59E0B' },
          { label: 'Quit zone (< 40)',    desc: 'Departure probability doubles every 30 days below this level.', color: '#DC2626' },
        ],
      },
      {
        type: 'cases',
        title: 'Case studies',
        items: [
          {
            title: 'Salary request from a senior',
            situation: 'Karim, your best driver (competence 85, satisfaction 70, 3 years seniority) asks for +€200/month. He already earns €200 above market average.',
            solution: 'Simple refusal → satisfaction -15 (drops to 55) → productivity falls. Acceptance → employer cost +€284/mo (×1.42 charges). Counter-offer: +€100 + an extra day off = perceived respect + cost contained.',
            lesson: 'Non-monetary counter-offers (training, leave, newer vehicle) are often worth as much as extra cash and cost 3× less.',
          },
          {
            title: 'Preventing burnout',
            situation: 'Sarah\'s satisfaction has dropped from 80 to 50 in 2 months. She did 18 trips in May (average 12). She just refused a weekend.',
            solution: 'Crossed signals: overload + refusal = imminent burnout. Immediate action: -1 mission/week for 4 weeks + 5-min interview + 1 exceptional day off. Cost: ~€600 of lost revenue. Avoids ~€11,600 of replacement cost.',
            lesson: 'Spot satisfaction ratios that drop FAST (>10 pts/month). Early intervention costs 20× less than a replacement.',
          },
          {
            title: 'Hire junior or senior?',
            situation: 'You have a €3,000/month budget (employer cost). You hesitate between a junior (competence 50, ~€2,600 cost) and a senior (competence 85, ~€3,200 cost).',
            solution: 'Junior: productivity ~50%, net margin ~€300/mo of revenue vs. cost. Senior: productivity ~85%, margin ~€800/mo. Senior generates 2.6× more margin but demands €230 more on hire.',
            lesson: 'On high-value contracts (premium, ADR, long distance), a senior quickly justifies the premium. For simple urban routes, juniors are enough.',
          },
          // ── 02/06/2026 — Mutual term. vs Dismissal case study ──
          {
            title: 'Parting ways with a driver: mutual termination or dismissal?',
            situation: 'A driver with 4 years seniority (gross €3,000/month) no longer fits: low performance but no serious fault. You want to part ways cleanly.',
            solution: 'Lawful dismissal "real & serious cause" → 1-month minimum procedure (summons, interview, notification), 2-month notice (~€8,500 loaded gross), ISRC ~€3,000, prud\'hommes risk. Total: ~€12-15,000. Mutual termination → 1 interview + DREETS approval (15 days), ISRC ~€3,000, **no notice**, **no possible litigation**. Total: ~€4,500.',
            lesson: 'For a no-conflict separation, **mutual termination saves 60-70% of total cost** and zero labour-court risk. Classic dismissal is justified when there is a real documented fault OR for economic reasons.',
          },
          {
            title: 'FCO is expiring: act now or wait?',
            situation: 'Driver Karim\'s FCO expires in 60 days. The course costs €600 and grounds him for 5 days. You have 3 contracts in progress.',
            solution: 'Launch FCO now: €600 + 5 days off-duty = ~€1,800 lost revenue. Total ~€2,400. Wait until expiry: DREAL fine €800-1,500 on next inspection (~1 month) + immediate truck impoundment → cancellation of running contracts → client penalties ~€5,000 + reputation -3.',
            lesson: 'All HSE trainings silently expire and cost **3-5× more reactively than preventively**. Set up a quarterly review of FCO / medical / ADR / CACES deadlines from the RSE page.',
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pitfalls to avoid',
        items: [
          {
            title: 'Systematically refusing raises',
            mistake: 'You see each request as an attack. You refuse them all to show firmness.',
            signal: 'Several drivers below 60 satisfaction simultaneously + multiple pending "renegotiation" messages.',
            fix: 'Build a TRANSPARENT salary grid (junior/confirmed/senior × specialty). Auto-approve requests ≤ 5% that match a crossed seniority threshold.',
          },
          {
            title: 'Overloading a reliable driver',
            mistake: 'Karim accepts everything. You stack the tough contracts and weekends on him. He cracks after 3 months.',
            signal: 'Trips/month ratio > 16 for a driver + satisfaction sliding.',
            fix: 'Cap at 14 trips/month per driver. Rotate hard missions between 2-3 people to share fatigue.',
          },
          {
            title: 'Not training',
            mistake: 'No training budget. Your juniors stay junior. Your seniors leave for elsewhere (where they are trained).',
            signal: 'No training launched for > 6 months + 1+ senior resignation.',
            fix: 'Training budget 1-2% of HR revenue. ADR / Refrigerated / Tacho = skills that unlock contracts at +25% margin AND increase driver loyalty.',
          },
          // ── 02/06/2026 — Legal pitfalls ──
          {
            title: 'Dismissing without real and serious cause',
            mistake: 'You dismiss a driver for "lack of motivation" or "difficult relationship". No written record, no warning, no documented reason.',
            signal: 'Hasty procedure, no prior warning email in the file, vague reason in the dismissal letter.',
            fix: 'The entire dismissal procedure must be DOCUMENTED in writing (emails, minutes). Without real and serious cause, the labour court typically awards **6-12 months of salary in damages**. Prefer **mutual termination**: no cause to prove, no possible challenge.',
          },
          {
            title: 'Letting HSE trainings expire (FCO / Medical / ADR / CACES)',
            mistake: 'Your drivers operate with FCO expired for 3 months. You plan to retrain them "soon".',
            signal: 'Red "expired" badge on Claire Maréchal\'s RSE page. Random DREAL fines accumulating.',
            fix: 'Schedule renewals **60 days BEFORE expiry** (the RSE page auto-alerts at D-60). Never tolerate any expired HSE training: 1 DREAL inspection = €800-1,500 + brand damage + criminal liability risk for the manager.',
          },
          {
            title: 'Confusing probation with firm CDI',
            mistake: 'After 7 months you want to "dismiss easily" as if still in probation. But the contract has automatically become a firm CDI.',
            signal: 'Seniority > 8 months but you never formally renewed the probation period (which is capped at 2 + 2 renewed months).',
            fix: 'The probation period is capped at 2 months (employee) or 4 months (manager). Renewal must be written AND allowed by the collective agreement. Beyond, it\'s a firm CDI: termination = mutual termination or classic dismissal with full procedure.',
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 5. TRANSPORT & LOGISTICS
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'transport',
    title: 'Transport & Logistics',
    subtitle: 'Incoterms, VAT, fleet, leasing',
    icon: 'Truck',
    color: '#0891B2',
    pages: [
      {
        type: 'cover',
        title: 'Transport & Logistics',
        intro: 'The job requires juggling 3 dimensions: regulatory (Incoterms, VAT), operational (fleet, occupancy) and financial (buy vs. lease). We cover them here.',
        bullets: [
          'Incoterms: who pays what, when?',
          'Intra vs. extra-EU VAT',
          'Outright purchase, loan, leasing: 3 strategies',
        ],
      },
      {
        type: 'definitions',
        title: 'Key definitions',
        items: [
          { term: 'Incoterm', def: 'International acronym defining the split of costs, risks and obligations between seller and buyer on a transport.' },
          { term: 'EXW', def: "Ex Works — the buyer takes over at the seller's plant. Maximum risk for the buyer." },
          { term: 'FCA', def: 'Free Carrier — seller delivers to the named carrier. Risk transfers at that point.' },
          { term: 'DAP', def: 'Delivered at Place — seller delivers to destination, import clearance on the buyer.' },
          { term: 'DDP', def: 'Delivered Duty Paid — seller delivers cleared. Maximum risk for the seller.' },
          { term: 'Intra-EU VAT', def: 'On B2B EU shipments: reverse charge. The customer declares VAT in their country.' },
          { term: 'Extra-EU VAT', def: 'Export outside EU: French VAT does not apply. Potential customs duties at destination.' },
          { term: 'Occupancy rate', def: 'Share of the fleet actively in use. < 60% = chronic under-use. > 85% = saturation, contract refusals.' },
        ],
      },
      {
        type: 'diagram',
        title: 'Incoterms 2020: where costs and risks transfer',
        intro: 'Each Incoterm defines two things: when costs transfer from seller to buyer, and when risk (loss, theft, damage) transfers. EXW = everything on the buyer. DDP = everything on the seller. Between the two, 4 major categories.',
        diagram: 'incoterm-bands',
        legend: [
          { label: 'EXW (Ex Works)', desc: 'Seller delivers at the plant. Buyer pays loading, transport, export + import customs.' },
          { label: 'FCA (Free Carrier)', desc: 'Seller delivers to carrier. Risk transfers at that point.' },
          { label: 'CPT/CIP (Carriage Paid To)', desc: 'Seller pays transport to destination — but risk transfers at departure.' },
          { label: 'DAP (Delivered at Place)', desc: 'Seller delivers to destination, import clearance on the buyer.' },
          { label: 'DDP (Delivered Duty Paid)', desc: "Seller delivers cleared at buyer's place. Maximum risk for the seller." },
        ],
      },
      {
        type: 'formulas',
        title: 'Calculation formulas',
        items: [
          {
            name: 'Total cost per km',
            formula: 'Cost/km = (Fuel + Tolls + Maintenance + Vehicle depreciation + Driver wage ÷ km/month)',
            example: 'For one 19t truck: ~€0.90/km all-in.',
          },
          {
            name: 'Contract margin',
            formula: 'Margin = (Contract price − total mission cost) ÷ Contract price × 100',
            example: 'Contract €1,200 over 800 km at €0.90/km = €720 cost → Margin = (1200-720)/1200 = 40%.',
          },
          {
            name: 'VAT on intra-EU delivery',
            formula: 'If B2B customer with valid VAT number: VAT = €0 (customer reverse-charge)\nOtherwise: French VAT 20%',
            example: 'Delivery Lyon → Milan for Fiat (valid Italian VAT): €0 of output VAT.',
          },
          {
            name: 'Purchase vs. lease comparison (3 years)',
            formula: 'Purchase cost = Price × (1 - resale) + loan interest\nLease cost = Rent × 36',
            example: 'Truck €60,000: purchase = 60k - 25k resale = 35k. Lease = 1,100 × 36 = €39,600. Purchase -10%.',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Glossary',
        items: [
          { word: 'ADR', def: 'European agreement for transport of dangerous goods. Requires driver certification.' },
          { word: 'CMR', def: 'International consignment note. Mandatory document for any cross-border road transport.' },
          { word: 'Cabotage', def: 'Domestic transport in a country other than the truck\'s country of registration. Limited to 3 missions per 7 days in the EU.' },
          { word: 'DREAL', def: 'French regional environment & infrastructure agency. Carries out transport technical inspections.' },
          { word: 'Eurovignette', def: 'Flat toll in some EU countries (Germany, Netherlands, Luxembourg) for HGVs.' },
          { word: 'GVW (PTAC)', def: 'Gross Vehicle Weight Authorised. Determines the vehicle category (3.5t — 19t — 26t — 44t).' },
          { word: 'TMS', def: 'Transport Management System. Software for planning and tracking operations.' },
          { word: 'Tachograph', def: 'Mandatory device recording driver driving and rest times.' },
        ],
      },
      {
        type: 'diagram',
        title: 'Buy, finance or lease?',
        intro: 'Over 3 years, the financing mode changes the total cost — and the balance-sheet impact. Here is a comparison to size for EACH vehicle.',
        diagram: 'flotte-comparatif',
        legend: [
          { label: 'Outright purchase', desc: 'Cheapest long-term but drains cash immediately.' },
          { label: 'Lease',              desc: 'Most expensive (+10-15%) but no initial cash outflow.', color: '#DC2626' },
          { label: 'Loan',               desc: 'Compromise: spreads the outflow + keeps ownership.', color: '#10B981' },
        ],
      },
      {
        type: 'cases',
        title: 'Case studies',
        items: [
          {
            title: 'Delivery Lyon → Milan (Fiat)',
            situation: 'You invoice €1,800 ex-VAT to Fiat (intra-EU B2B with valid Italian VAT number). Mission cost: €1,100.',
            solution: 'Reverse charge: you invoice €1,800 ex-VAT without French VAT. Fiat declares Italian VAT in its country. Gross margin = (1800 − 1100) / 1800 = 39%. Mandatory invoice note: "Reverse charge — Art. 196 Directive 2006/112/EC".',
            lesson: 'ALWAYS verify the customer\'s intra-EU VAT number via VIES.europa.eu before the first invoice. An invalid number = French VAT to remit from your own pocket.',
          },
          {
            title: 'Which Incoterm to choose?',
            situation: 'You transport Bordeaux wine for a Swiss customer. Swiss customs clearance in Geneva is tricky.',
            solution: 'DDP (Delivered Duty Paid) = you handle everything, including Swiss customs → high price but simple for the customer. DAP = you deliver, but the customer handles customs → cheaper for you, more risk for the customer. Recommendation: DAP with a dedicated broker.',
            lesson: 'A clear Incoterm on the invoice avoids 90% of disputes. EXW (least risky for the seller) — DDP (most risky).',
          },
          {
            title: 'Optimising occupancy rate',
            situation: 'Your 5 trucks run at 55% occupancy. You lose €800/month per truck on uncovered fixed charges.',
            solution: 'Action 1: temporarily refuse premium contracts with tight deadlines that occupy poorly. Action 2: price more aggressively (-5%) to win filler contracts. Action 3 (radical): sell 1 truck to move to 4 at 75% occupancy = +50% margin.',
            lesson: 'An under-used fleet = cash evaporating. Size is not the goal — occupancy is.',
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pitfalls to avoid',
        items: [
          {
            title: 'Over-buying at cycle top',
            mistake: 'Activity is booming, you order 3 new trucks. 6 months later, activity falls back and 2 trucks run empty.',
            signal: 'Revenue growth > 25% over a quarter + fleet purchase without durability test.',
            fix: 'Before each purchase, require 60 days of > 85% occupancy CONSECUTIVELY on the existing trucks. If demand spikes, take a short lease (12 months) to check durability.',
          },
          {
            title: 'Ignoring cabotage rules',
            mistake: 'Your French-registered truck does 5 consecutive missions in Germany in 1 week.',
            signal: 'Customs notification "Abusive cabotage detected" + fine €1,000-5,000.',
            fix: 'EU limit: 3 intra-country missions per 7 days for a foreign-registered truck. Beyond, return empty to country of registration or invoice via a local subsidiary.',
          },
          {
            title: 'Confusing gross margin with contract margin',
            mistake: 'You compute the overall company margin. You accept a contract at 5% gross margin thinking it contributes.',
            signal: 'Contract with gross margin < average company gross margin = dilution signal.',
            fix: 'Each contract must have a gross margin >= 30%. Below, you dilute performance AND tie up a resource for nothing.',
          },
          {
            title: 'Cluttering the dashboard with too many opportunities',
            mistake: 'You let 20+ contracts stack up in the opportunities list. The visual noise hides the real gems and you lose 10 min on every review.',
            signal: 'More than 15 simultaneous contracts in "Available contracts" + several contracts you know you will never accept (too short, wrong specialty, margin < threshold).',
            fix: 'Use the **X** button on each contract to hide it immediately (reversible via "Restore N"). Keep 5-7 opportunities on screen. This quick filter divides decision time by 3 without losing contracts — they just stay out of sight.',
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 6. M&A & STRATEGY
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'ma',
    title: 'M&A & Strategy',
    subtitle: 'Due diligence, LOI, valuation, antitrust',
    icon: 'Handshake',
    color: '#DB2777',
    pages: [
      {
        type: 'cover',
        title: 'M&A & Strategy',
        intro: 'Acquiring a competitor can multiply your size in months — or bury you in debt if you overpay. The art of M&A is paying the fair price at the right time.',
        bullets: [
          'The 5 steps of an acquisition',
          'Valuation calculation',
          'Antitrust risks and driver retention',
        ],
      },
      {
        type: 'definitions',
        title: 'Key definitions',
        items: [
          { term: 'M&A', def: "Mergers & Acquisitions. External growth by buying a competitor." },
          { term: 'Due diligence', def: 'Full audit of the target before committing: finance, contracts, HR, litigation. First step.' },
          { term: 'LOI', def: 'Letter of Intent. Non-binding document that formalises negotiations and grants temporary exclusivity.' },
          { term: 'Valuation', def: 'Price considered fair for the target. Several methods: revenue multiple, EBITDA multiple, DCF.' },
          { term: 'Antitrust', def: 'Anti-concentration regulation. The Competition Authority can block a takeover that creates a local monopoly.' },
          { term: 'Closing', def: 'Final signature at the notary. Effective transfer of assets and control.' },
          { term: 'Earn-out', def: 'Price top-up conditional on hitting post-acquisition targets. Protects the acquirer.' },
          { term: 'Goodwill', def: 'Premium paid beyond net book assets, in exchange for the brand, the contracts, the team.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Calculation formulas',
        items: [
          {
            name: 'Indicative purchase price (HST method)',
            formula: 'Price = 0.5 × Annual_revenue + 0.8 × Fleet_resale_value + 1.2 × Active_contracts_value + Net_debt',
            example: 'Revenue 60k/month × 12 = 720k → 0.5 × 720k = 360k\n10 vehicles at 35k = 350k → 0.8 × 350k = 280k\n15 contracts at 4.5k = 67.5k → × 1.2 = 81k\nNet debt: 80k\nTotal ≈ €801,000',
          },
          {
            name: 'EBITDA multiple (classic method)',
            formula: 'Valuation = EBITDA × multiple (transport: 4-6×)',
            example: 'EBITDA €80,000/year × multiple 5 = €400,000 valuation.',
          },
          {
            name: 'Combined market share (antitrust)',
            formula: 'Combined_MS = (Buyer_revenue + Target_revenue) ÷ Local_market_revenue',
            example: 'Revenue 40k + 30k = 70k / Market 200k = 35% → amber zone (close review).',
          },
          {
            name: 'Driver retention rate post-acquisition',
            formula: 'Retention = base_satisfaction + buyer_reputation_modifier\nbase: sat>60 → 90% | 40-60 → 60% | <40 → 25%',
            example: 'Buyer with reputation 80/100: modifier = +6%\nDriver sat 75 → 96% chance to stay.',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Glossary',
        items: [
          { word: 'Asset deal', def: 'Acquisition of targeted assets (fleet, contracts) without buying the whole company. More flexible tax-wise.' },
          { word: 'Legal audit', def: 'Review of articles, contracts, intellectual property, ongoing litigation.' },
          { word: 'Business sale (cession de fonds)', def: 'Sale of the business as a going concern: clientele, brand, contracts, equipment. Broader than an asset deal.' },
          { word: 'Non-compete clause', def: "Seller's commitment not to resume the same activity in a given zone for X years." },
          { word: 'Concentration', def: 'Operation that durably modifies the control of a company. Must be notified if thresholds are crossed.' },
          { word: 'Net debt', def: 'Financial debts − treasury. Measures true indebtedness.' },
          { word: 'Judicial administrator', def: 'Ministerial officer who runs a liquidation or restructuring. Sells assets to repay creditors.' },
          { word: "Shareholders' agreement", def: 'Private agreement between partners on governance, exits, voting rights.' },
          { word: 'Share deal', def: 'Acquisition of shares. Full takeover, specific capital gains tax.' },
          { word: 'Synergy', def: 'Saving or revenue gain generated by the union (bulk purchasing, cross-selling, fleet optimisation).' },
        ],
      },
      {
        type: 'diagram',
        title: 'The M&A funnel',
        intro: 'Out of 100 identified targets, only a few make it to closing. Most of the work is filtering out weak files quickly.',
        diagram: 'ma-funnel',
        legend: [
          { label: 'Targets',    desc: '100 companies sourced by segment / geography.' },
          { label: 'NDA',        desc: '~30 accept a confidentiality agreement to share figures.' },
          { label: 'Due dil.',   desc: '~10 pass financial and operational audit.' },
          { label: 'LOI',        desc: '~3-4 sign a letter of intent with exclusivity.' },
          { label: 'SPA',        desc: '~1-2 negotiate a definitive Sale & Purchase Agreement.' },
          { label: 'Closing',    desc: '1 effective closing at the notary.' },
        ],
      },
      {
        type: 'cases',
        title: 'Case studies',
        items: [
          {
            title: 'First offer — how much to propose?',
            situation: 'Target: 8 trucks, revenue €720k/year, EBITDA €60k, net debt €80k. Seller asks €1.2M.',
            solution: 'EBITDA multiple method (transport: 4-6×) → 60 × 5 = 300k + net debt to assume = 380k. HST method → ~€800k (see formula). Make an offer of €600k with €200k earn-out conditional on driver retention > 80% over 18 months. The seller sees €800k "possible" but you cap your risk.',
            lesson: 'NEVER pay in cash what an earn-out can finance. The earn-out aligns the seller on the transition.',
          },
          {
            title: 'Keeping drivers after the acquisition',
            situation: 'You acquire an SME with 12 drivers. Average satisfaction: 55. Your reputation: 70.',
            solution: 'Retention modifier = +6%. Base sat 55 (zone 40-60) = 60% natural retention = 66% after modifier. So ~8 drivers kept out of 12. Action: 1-on-1 with each within 15 days + merger bonus (~€500) for strategic seniors.',
            lesson: 'Worst-case scenario in an acquisition: paying the target and losing the drivers in 3 months. Invest €5-10k in retention bonuses systematically.',
          },
          {
            title: 'Antitrust — when to worry',
            situation: 'You acquire the #3 regional. You were #2. Local Lyon market = €4M. You = €1.2M. Target = €0.8M.',
            solution: 'Combined MS = (1.2 + 0.8) / 4 = 50%. Above 30%, the Competition Authority examines. ANTICOMP notification is mandatory if the threshold is crossed. Prepare a file of partial contract divestments to a third party to bring it back to ~40%.',
            lesson: 'Anticipate antitrust as early as due dil.: it is not a formality, it can derail the deal at the last minute.',
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pitfalls to avoid',
        items: [
          {
            title: 'Paying cash for a high goodwill',
            mistake: "You pay €600k for a company with €200k net assets. The goodwill (€400k) must be amortised over 10 years → €40k/year of charge weighing on the income statement.",
            signal: 'Goodwill > 50% of purchase price = over-payment signal.',
            fix: 'Justify every euro of goodwill with a quantified synergy (contracts, team, patent, brand). Otherwise, negotiate down.',
          },
          {
            title: 'Ignoring off-balance-sheet commitments',
            mistake: 'You acquire a company with hidden leases. 6 months later, you discover €240k of commitments to pay over 3 years.',
            signal: 'Class 8 accounts (commitments) absent or thin in the tax bundle = red flag.',
            fix: 'Require the exhaustive list of lease contracts, rentals, guarantees given BEFORE the LOI. Insert a representation & warranty clause for hidden liabilities.',
          },
          {
            title: 'Neglecting cultural integration',
            mistake: 'You merge two teams without a meeting. Drivers from each camp ignore or distrust each other. Social climate in free fall.',
            signal: 'Resignations ×2 within 90 days post-closing + inbox flooded with cross-complaints.',
            fix: '100-day integration plan: team-building day on D+30, unified badges/uniforms by D+60, joint committee on D+90. Budget €5-10k that is worth €50k.\n\n**HST tip**: for each driver acquired in a merger, consult their **enriched profile card** (photo + bio + personality archetype — old-school veteran, motivated rookie, methodical, etc.). The **M&A** badge appears on the card. Tailor your first interviews to these traits — an "old-school veteran" does not react like a "motivated rookie". 80% of retention is decided in the tone of the first conversation.',
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 7. SALES & COMMERCIAL
  // (Prospection, MEDDIC, RFQ detection, BO support) — 04/06/2026
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'commercial',
    title: 'Sales & Commercial',
    subtitle: 'Prospection, MEDDIC, RFQs, BO support',
    icon: 'Briefcase',
    color: '#F59E0B',
    pages: [
      {
        type: 'cover',
        title: 'Sales & Commercial',
        intro: "Without new clients, your order book erodes mechanically. Sales is not a cost — it is the engine fuelling every other function (fleet, HR, finance). Learn to prospect, qualify and argue methodically.",
        bullets: [
          'B2B sales cycle and the MEDDIC qualification method',
          'Prospection : sources, channels, ratios',
          'Detecting and qualifying RFQs / tenders',
          'Back-office support roles : who does what',
        ],
      },
      {
        type: 'definitions',
        title: 'Key concepts',
        items: [
          { term: 'Sales pipeline',
            def: 'Aggregated view of open opportunities by stage (lead → contact → quote → negotiation → signature). A healthy pipeline holds 3-5× the revenue target (typical conversion 20-30 %).' },
          { term: 'Lead',
            def: 'Identified contact with qualified contact info and need. Different from a "raw lead" (just a name, not yet qualified).' },
          { term: 'MEDDIC',
            def: 'B2B qualification framework : Metrics, Economic buyer, Decision criteria, Decision process, Identify pain, Champion. Each letter = a question. Saves 3 months on dead opportunities.' },
          { term: 'Sales funnel',
            def: 'Visual representation of prospect → customer journey. Each step has a conversion rate, e.g. 100 leads → 30 meetings → 10 quotes → 3 signatures = 3 % overall.' },
          { term: 'Sales cycle',
            def: 'Average time from first contact to signature. B2B transport SMB : 4-8 weeks. Large accounts / public RFQs : 3-9 months.' },
          { term: 'RFQ / Tender',
            def: 'Formal procedure where a buyer (private or public) solicits multiple providers against precise specs. Private negotiated market OR regulated public tender (BOAMP, OJEU).' },
          { term: 'BOAMP / OJEU',
            def: 'Bulletin Officiel des Annonces de Marchés Publics (France) and Official Journal of the European Union. Mandatory publication platforms above given thresholds.' },
          { term: 'Technical memorandum',
            def: 'Core document of a tender response. Describes means, organisation, quality, CSR. Typically 40-60 % of the final score (vs price 30-40 %).' },
        ],
      },
      {
        type: 'formulas',
        title: 'B2B sales cycle in transport',
        items: [
          {
            name: 'Step 1 — Prospection',
            formula: 'Sources : LinkedIn, trade shows, referrals, BOAMP, CCI databases',
            example: 'Regional transport : target logistics directors and procurement teams in BTP, agri-food, retail. Typical monthly volume : 50 cold contacts = 5 qualified meetings.',
          },
          {
            name: 'Step 2 — Qualification (MEDDIC)',
            formula: 'Metrics : volume, frequency, distance. Economic buyer : who signs ? Decision criteria : price / lead time / quality / CSR ? Decision process : RFQ or direct ? Identify pain : what is broken with their current carrier ?',
            example: 'If you cannot answer all 6 MEDDIC letters after 2 meetings, the opportunity is not ripe. Put it aside and come back in 3 months.',
          },
          {
            name: 'Step 3 — Quote & negotiation',
            formula: 'Price = (direct cost × 1.35) + target margin. Argue on the client\'s primary criterion (cf. argumentation mini-game).',
            example: 'Industrial SMB : price-sensitive. Public/Health : sensitive to documented quality. Export : sensitive to deadlines.',
          },
          {
            name: 'Step 4 — Closing',
            formula: 'Address final 3 friction points : price, payment terms, references. Offer a 3-month pilot contract if the prospect is wary.',
            example: '85 % of lost deals are lost due to lack of follow-up between meetings. Follow up D+3, D+7, D+15.',
          },
        ],
      },
      {
        type: 'formulas',
        title: 'Tender detection',
        items: [
          {
            name: 'Public sources (free)',
            formula: 'BOAMP.fr (national) · marches-publics.gouv.fr · OJEU (European) · regional platforms (PLACE, AWS)',
            example: 'Transport market > €40k mandatorily published on BOAMP. Filter by keyword : "transport routier", "messagerie", "logistique", "frigorifique".',
          },
          {
            name: 'Private sources (subscription)',
            formula: 'Vecteur Plus, Doubletrade, ProDouane — automated alerts on target profiles',
            example: '€100-300/month depending on coverage. Pays off after the first won tender (~€50-200k contract).',
          },
          {
            name: 'Informal sources',
            formula: 'Network, LinkedIn, trade shows (SITL Paris, Tranzit, IT-Trans). Competitive watch : losing customers = opportunity',
            example: 'An experienced sales rep captures 30-40 % of their tenders off-market through their network.',
          },
          {
            name: 'Qualify quickly (in 15 minutes)',
            formula: '1. Volume vs my capacity — 2. CCAP / penalties acceptable ? — 3. Realistic deadline ? — 4. Do I have references ? — 5. Will I make my margin ?',
            example: 'If a single answer is "no" : do not bid. Tender selection matters as much as response quality.',
          },
        ],
      },
      {
        type: 'definitions',
        title: 'Commercial back-office supports',
        items: [
          { term: 'Rémi Forni — Marketing',
            def: 'Generates leads via targeted campaigns and trade-show presence. +1 to +2 pts of awareness/month, +2 visible contracts. Essential at Phase 2 start.' },
          { term: 'Sherazade Fetitou — Communication',
            def: 'Builds employer brand + customer reputation (+1 contract). Long-term effect on LTV and conversion rate.' },
          { term: 'Léa Bertrand — Junior sales',
            def: 'Field prospection + phone qualification. +2 visible contracts, unlocks the argumentation mini-game skip. First sales hire from >€5k/month.' },
          { term: 'Marc Dupuis — Senior sales',
            def: 'Key account management + complex tender responses. +3 visible contracts. Hire when a junior plateaus (around €15-25k/month).' },
          { term: 'Sylvie Marchand — Sales expert',
            def: 'Top-tier closing + Tier 4-5 negotiation. Unlocks premium international contracts. Phase 3-4 hire.' },
          { term: 'Technical memorandum (writing)',
            def: 'For a complex tender (> €200k), writing the memo takes 5-10 person-days. Marc or Sylvie handle it ; without them, you do.' },
          { term: 'CRM (tool)',
            def: 'Customer Relationship Management — central database of all customer interactions. Without CRM : 30 % of opportunities slip through the cracks. Worth unlocking from 5-10 active clients.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Commercial KPIs to monitor',
        items: [
          {
            name: 'Conversion rate',
            formula: 'Signatures ÷ Quotes sent × 100',
            example: 'B2B transport : 25-35 % is good, > 40 % suggests under-pricing or over-selectivity.',
          },
          {
            name: 'Customer acquisition cost (CAC)',
            formula: '(Marketing + Sales salaries + Trade-show fees) ÷ New customers',
            example: 'Target : CAC ≤ 15-20 % of annual revenue per customer. €8k CAC for a €30k/year client = healthy 1:3.75 ratio.',
          },
          {
            name: 'Pipeline coverage',
            formula: 'Pipeline forecast revenue ÷ Revenue target',
            example: 'Healthy coverage : 3-5× target (absorbs the 70-80 % of opportunities that will not convert). If < 2× : red alert, accelerate prospection.',
          },
          {
            name: 'Net Promoter Score (NPS)',
            formula: 'Promoters (9-10) - Detractors (0-6), out of 100',
            example: 'NPS > +30 = excellent in B2B. Quarterly survey. Passive booster of word-of-mouth (= notoriety in-game).',
          },
        ],
      },
      {
        type: 'definitions',
        title: 'Pitfalls to avoid',
        items: [
          { term: 'The engagement tunnel',
            def: 'Continuing to invest time and energy on a warm prospect that never decides. > 4 months without tangible progress : close or archive.' },
          { term: 'The "spray and pray" quote',
            def: 'Sending 50 quotes without prior qualification. Result : 0 signatures and a ridiculous conversion rate. Qualify BEFORE pricing.' },
          { term: 'The suicidal teaser price',
            def: 'Slashing prices to win a first contract. Consequence : negative margin, escalating demands, customer leaves the moment a cheaper option appears. Prefer reduced volume at fair price.' },
          { term: 'Ignoring weak signals',
            def: 'A growing SMB, a procurement change, a new depot opening = signals. The sales rep who spots them first wins the best deals.' },
          { term: 'Underestimating the technical memorandum',
            def: 'A public tender is won on the memo quality (40-60 % of the score). Rushing it = handing easy points to a competitor who took theirs seriously.' },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Vocabulary',
        items: [
          { word: 'Qualified lead', def: 'A contact whose need, budget and decision-maker are identified (MQL = Marketing Qualified Lead, SQL = Sales Qualified Lead).' },
          { word: 'Cold call', def: 'Call to a prospect with no prior contact. B2B average : 1-3 % conversion to meeting.' },
          { word: 'Closing', def: 'Final signature step. Techniques : urgency, social proof, alternative, silence.' },
          { word: 'Up-sell / Cross-sell', def: 'Selling a premium tier (up) or a complementary service (cross) to an existing client. CAC divided by 5 vs new client.' },
          { word: 'CCAP / CCTP', def: 'Administrative & Technical Particular Clauses (FR public tenders) — contractual documents to read fully before bidding.' },
          { word: 'OPEX vs CAPEX', def: 'Opex = operational expenses (fuel, HR). Capex = investments (vehicles, depots). Buyers often arbitrate between the two.' },
          { word: 'CRM', def: 'Customer Relationship Management. Software or methodology to centralise customer interactions.' },
        ],
      },
    ],
  },


  // ════════════════════════════════════════════════════════════════════
  // 7. CUSTOMERS & LOYALTY (CRM)
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'clients',
    title: 'Customers & Loyalty',
    subtitle: 'CRM, tiers, lifetime value',
    icon: 'UserCircle',
    color: '#0EA5E9',
    pages: [
      {
        type: 'cover',
        title: 'Customers & Loyalty',
        intro: 'Winning a customer costs 5× more than keeping one. A loyal Partner recommends you, accepts your prices, and lets your small glitches slide. Cultivating the portfolio = snowball effect.',
        bullets: [
          '5 relationship tiers: Suspicious → Partner',
          'Actions that lift / drop the score',
          'Lifetime Value (LTV) of a customer',
        ],
      },
      {
        type: 'definitions',
        title: 'Key definitions',
        items: [
          { term: 'Loyalty score (0-100)', def: 'Aggregated measure of customer relationship quality. Evolves with each interaction (delivery, delay, cancellation, recommendation).' },
          { term: 'Suspicious tier (0-20)', def: 'Customer losing trust. 30-day cooldown during which no new contracts are offered. Threat of termination.' },
          { term: 'Neutral tier (20-40)',  def: 'Relationship onboarding. Market standards, no special advantage.' },
          { term: 'Confident tier (40-65)',def: 'Trust established. Smooth renewals, relaxed communication.' },
          { term: 'Loyal tier (65-85)',    def: 'Open preference. You get extra missions in exchange for a -2% discount (perceived "trusted partner").' },
          { term: 'Partner tier (85-100)', def: 'Top of mind. The customer recommends you, invites you to their events, pays without negotiating.' },
          { term: 'Lifetime Value (LTV)',  def: 'Total revenue predictable over the relationship duration. A Loyal customer is worth ~5× a Neutral one over 3 years.' },
          { term: 'Suspicious cooldown',   def: 'Auto 30-day cool-off after dropping to Suspicious. The customer closes the door on new proposals.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Calculation formulas',
        items: [
          {
            name: 'Score evolution (per event)',
            formula: '+2 on-time delivery · +1 weekly delivery in progress · −4 delay · −15 contract cancellation · +10 year without incident · +0.5 reputation > 70',
            example: 'A 30-day contract delivered without a hitch: 4 × (+1 weekly) + 1 × (+2 final) = +6 pts. A cancellation wipes out ~15 weeks of work.',
          },
          {
            name: 'Price multiplier by tier',
            formula: 'Suspicious ×1.0 — Neutral ×1.0 — Confident ×1.0 — Loyal ×0.98 — Partner ×0.98',
            example: 'A Loyal customer pays -2% vs. list. Largely offset by their LTV (volume × duration).',
          },
          {
            name: 'Estimated Lifetime Value',
            formula: 'LTV ≈ monthly_revenue × relationship_duration_months × renewal_probability',
            example: 'Confident: €2,000/mo × 24 months × 0.75 = €36,000. Partner: €2,000 × 60 × 0.95 = €114,000. So ×3.2.',
          },
          {
            name: 'Cost of a customer loss',
            formula: 'Cost = remaining LTV + cost to acquire a replacement (typ. €800-2,000)',
            example: 'Loss of a Loyal at €40k remaining LTV = direct loss + €1,500 to acquire a Neutral replacement who will take 2 years to reach Loyal.',
          },
        ],
      },
      {
        type: 'diagram',
        title: 'The 5 loyalty tiers',
        intro: 'Each tier unlocks different behaviours: a Partner recommends you, a Suspicious threatens to terminate.',
        diagram: 'client-tiers',
        legend: [
          { label: 'Suspicious', desc: 'Formal notice, threats, no longer proposes contracts.',          color: '#DC2626' },
          { label: 'Neutral',    desc: 'Standard, no particular emotion.',                              color: '#9CA3AF' },
          { label: 'Confident',  desc: 'Easy renewals, occasional thanks.',                             color: '#0891B2' },
          { label: 'Loyal',      desc: 'Open preference, offers extra missions.',                       color: '#D97706' },
          { label: 'Partner',    desc: 'Recommendations, gifts, VIP invitations.',                      color: '#10B981' },
        ],
      },
      {
        type: 'cases',
        title: 'Case studies',
        items: [
          {
            title: 'Rescuing a Suspicious customer',
            situation: 'TransLog SA dropped to 12/100 (Suspicious) after 2 consecutive delays. The 30-day cooldown blocks new contracts.',
            solution: "Step 1: write a personalised apology email (template `cli-mefiant-complaint-01` choice `apologise`) + offer a €300 credit. Step 2: on the ongoing contract, assign your best driver. Step 3: wait for the end of the cooldown then offer a contract at -5% to restart trust. ~60 days to climb back to Neutral.",
            lesson: 'A Suspicious customer is not lost. You need a concrete and visible action. Invest €500-1,000 to save a €20k LTV.',
          },
          {
            title: 'Balanced portfolio strategy',
            situation: 'You have 10 active customers: 6 Neutrals, 2 Confidents, 1 Loyal, 1 Suspicious. Your revenue depends 50% on the Loyal.',
            solution: 'Concentrated risk: if the Loyal leaves, -50% revenue overnight. Action: lift 2-3 Confidents to Loyal in parallel. Plan: impeccable quality + a symbolic gift (a featured Bordeaux box) in year 1 + extra-mission proposed systematically.',
            lesson: 'Aim for 3+ Loyals in your portfolio. No customer should represent > 25% of total revenue.',
          },
          {
            title: 'Declining a contract without offending',
            situation: "Marie's Cuisine (Confident, 55) offers you a one-off mission. Your fleet is saturated. A flat refusal = -3 pts.",
            solution: 'Instead of a blunt refusal: recommend a trusted peer (who will return the favour). Cost: €0. Benefit: the score drops only -1 instead of -3, and the customer perceives your honesty.',
            lesson: 'A skilful "no" is worth a "yes". Building a network of partner carriers lets you handle saturation without breaking the relationship.',
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pitfalls to avoid',
        items: [
          {
            title: 'Betting it all on 1 big account',
            mistake: 'The big account represents 60% of your revenue. You adapt everything to its demands.',
            signal: 'Top 1 customer > 30% of monthly revenue for 3 consecutive months.',
            fix: 'As soon as a customer exceeds 25% of revenue, launch active prospection on 2-3 new segments. Diversify before they decide for you.',
          },
          {
            title: 'Discounting to recover a Suspicious',
            mistake: 'To soothe a Suspicious, you offer -20% on the next contract. You lose margin without rebuilding trust (the service did not change).',
            signal: 'Discount > 10% on an existing contract = bandage, not cure.',
            fix: 'A discount is NOT the lever for restoring trust. Commit to a VERIFIABLE INDICATOR (0 delays over 30 days, dedicated driver, weekly report). It costs less and actually rebuilds.',
          },
          {
            title: 'Neglecting Confidents in favour of Loyals',
            mistake: 'You pamper your 2 Loyals and forget your 5 Confidents. 6 months later, 3 have dropped to Neutral.',
            signal: 'No contextual message sent to a Confident customer for > 60 days.',
            fix: 'Monthly routine: 1 personalised message to EACH Confident customer (newsletter, project update, market info). Cost 0, huge return.',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Glossary',
        items: [
          { word: 'Account manager', def: 'Person in charge of a key customer\'s follow-up. For an SME: the founder at the beginning.' },
          { word: 'Churn',           def: 'Attrition rate. % of customers lost over a given period.' },
          { word: 'Cross-selling',   def: 'Selling a complementary service to an existing customer (e.g. adding refrigerated to a messaging customer).' },
          { word: 'KPI',             def: 'Key Performance Indicator. Steering metric (monthly revenue, NPS, churn, LTV).' },
          { word: 'NPS',             def: 'Net Promoter Score. Recommendation measure (0 to 100). > 50 = excellent.' },
          { word: 'Onboarding',      def: 'Integration phase of a new customer. Critical: 80% of long-term opinion is formed in the first 90 days.' },
          { word: 'RFP',             def: 'Request For Proposal — formal tender. You submit a detailed written bid.' },
          { word: 'Up-selling',      def: 'Moving a customer up-market (e.g. from a 30-day contract to a 90-day framed contract).' },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 8. TENDERS / CALLS FOR TENDER — 02/06/2026
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'appels-offres',
    title: 'Tenders',
    subtitle: 'Long-term contracts, bidding, negotiation',
    icon: 'FileSignature',
    color: '#10B981',
    pages: [
      {
        type: 'cover',
        title: 'How tenders work (FR context, EU-wide patterns)',
        intro: "A tender (Appel d'Offres / AO in France) is a formal process by which a buyer (private or public) puts multiple providers in competition for a long-term contract. It's the gateway to large accounts (local authorities, key accounts, public markets). Win one well-prepared bid = 6 to 18 months of guaranteed bookings.",
        bullets: [
          '3 tiers: local (city halls, SMEs) → regional (co-ops, mid-caps) → national (key accounts, public markets)',
          'Multi-criteria scoring: price, CSR, financial reliability, references',
          'Negotiation phase possible (client counter-offer)',
          'Fixed duration imposed by the client (90 to 540 days)',
        ],
      },
      {
        type: 'definitions',
        title: 'Key definitions',
        items: [
          { term: 'AO / Tender',           def: 'Formal competitive bidding procedure. Buyer publishes the specs, providers submit, buyer compares and selects.' },
          { term: 'Specification document', def: 'Document detailing expectations: scope, volumes, quality/HSE/CSR constraints, scoring criteria, contract duration, pricing terms.' },
          { term: 'Public market',         def: 'Tender from a public body (mayor, region, State, hospitals, SNCF…). Governed by the French Public Procurement Code. Weighted criteria (price + technical + CSR) mandatory above €40k.' },
          { term: 'Private market',        def: 'Tender from a private company. More flexible in form, but competition is still fierce (typically 3-5 bidders).' },
          { term: 'BAFO (Best And Final Offer)', def: 'Final negotiation round: buyer asks its 2-3 finalists for their best final bid. Typical phase on €100k+ markets.' },
          { term: 'Weighted criteria',     def: 'Transparent scoring: e.g. price 40% / CSR 30% / technical 30%. Each criterion scores, weighted sum = final note.' },
          { term: 'EcoVadis',              def: 'International CSR rating standard (Bronze → Silver → Gold → Platinum). Increasingly required by key accounts as an eligibility gate.' },
          { term: 'Approved supplier',     def: '"Validated provider" status with a key account after winning a tender. Opens complementary markets without a full re-bid.' },
          { term: 'Counter-offer',         def: 'Buyer comes back with a price below your bid (typically -5 to -10%). You arbitrate: accept, refuse (and lose), or push back.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Scoring formulas',
        items: [
          {
            name: 'HST win probability model (simplified)',
            formula: 'P = 0.50 + ΔRep + ΔPrice + ΔCSR + ΔFleet + ΔFin + ΔHSE',
            example: 'Clamped between 5% and 95%. ΔPrice = ±20 (bid at -20% of ref price = +0.20; +30% = -0.20). ΔRep = ±18 (reputation × notoriety). ΔCSR = +15 max from CSR score + EcoVadis tier. ΔHSE = +7 or +15 for moderate or strong HSE commitment.',
          },
          {
            name: 'Typical public market final score',
            formula: '0.4 × price_score + 0.3 × technical_score + 0.3 × CSR_score',
            example: 'Bidder A: price 18/20 + tech 16/20 + CSR 14/20 = 16.4. Bidder B: price 14/20 + tech 18/20 + CSR 18/20 = 16.4 (tied). Price alone no longer wins.',
          },
          {
            name: 'Revenue secured by a tender',
            formula: 'Daily price × Duration set by client (days)',
            example: 'MecAero tender: €340/day × 270 days = €91,800 of guaranteed revenue over 9 months. Net margin to model after operating costs (~30-35% on average).',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Tender vocabulary',
        items: [
          { word: 'RFP',                    def: 'Request For Proposal. Anglo-Saxon equivalent of the French "AO" (Appel d\'Offres).' },
          { word: 'Restricted tender',      def: 'Market where only pre-selected providers can bid (vs. open tender where anyone can apply).' },
          { word: 'CCAP',                   def: 'Cahier des Clauses Administratives Particulières. Legal annex to a French public market.' },
          { word: 'CCTP',                   def: 'Cahier des Clauses Techniques Particulières. Describes expected services technically.' },
          { word: 'DCE',                    def: 'Dossier de Consultation des Entreprises. Full pack given to candidates: rules, CCAP, CCTP, BPU.' },
          { word: 'BPU',                    def: 'Bordereau des Prix Unitaires. Item-by-item price grid, basis for candidate comparison.' },
          { word: 'DUME',                   def: 'European Single Procurement Document. Self-declaration of eligibility for a public market.' },
          { word: 'Transparent scoring',    def: 'Obligation for the public buyer to publish the scoring grid before bids and explain decisions afterward.' },
          { word: 'Pre-contract appeal',    def: 'Legal action allowing a losing bidder to contest a public market award (Administrative Court).' },
        ],
      },
      {
        type: 'concept',
        title: 'The 3 phases of a tender',
        sections: [
          {
            title: '① Publication & dossier (D-30 to D0)',
            content: "Buyer publishes the DCE (Consultation Dossier). You read the specs, check your eligibility (revenue, references, certifications) and prepare your bid. In HST, this is the Tenders screen: 3 cards visible with narrative, fixed duration and prerequisites (min fleet, min CSR, min medical %).",
          },
          {
            title: '② Submission & analysis (D0 to D+8/14)',
            content: "You submit your offer (daily price + HSE commitment). The buyer compares candidates against weighted criteria. In HST, the win probability is computed live from 6 factors and rolls between D+8 and D+14. If the buyer is interested but hesitant, they come back with a -8% counter-offer.",
          },
          {
            title: '③ Award & execution',
            content: "Buyer decides: market won (firm contract signed), declined (you lose), or BAFO round on very large markets. In HST, a won tender becomes a recurring (regular) contract in the Market screen: you still need to assign it to a vehicle + compatible driver to start the work.",
          },
        ],
      },
      {
        type: 'cases',
        title: 'Practical cases',
        items: [
          {
            title: 'Should you bid on EVERY tender?',
            situation: "Your book is 70% full. Marc Dupuis surfaces 3 tenders simultaneously: City of Tours (local), Aquitaine Co-op (regional), MecAero (regional). You can only execute 2 max.",
            solution: "No — scattering kills win rate AND execution quality. Winning strategy: prioritise 1-2 tenders per month aligned with your prerequisites (fleet, CSR, available drivers). Declining a tender carries no penalty (vs. signing then under-delivering, which drops your reputation 5-10 points).",
            lesson: "One well-prepared bid beats three rushed ones. Focus your commercial function on a small number of highly targeted dossiers.",
          },
          {
            title: 'Low price vs. strong HSE commitment',
            situation: "You're bidding on Bio&Frais Coopérative (€245/day ref, 180-day duration). Choice: (A) €220/day + standard HSE, or (B) €250/day + strong HSE commitment (quarterly audit + 25% green fleet).",
            solution: "Option B is usually the winner on CSR-sensitive clients. ΔPrice from A to B = -0.12; but ΔHSE = +0.15. Net +0.03 on probability. AND you avoid the low-price trap that locks you into a downmarket positioning. Conversely, on Bricoland (low-cost distribution), price dominates.",
            lesson: "Tailor your strategy to the client profile: CSR-sensitive → strong HSE commitment; low-cost → aggressive pricing. No one-size-fits-all.",
          },
          {
            title: 'Accept or decline a counter-offer?',
            situation: "You bid MecAero at €340/day × 270 days (€91,800). Client returns with €313/day (€84,510). That's -8%. Initial expected margin: 32%; at €313/day, margin drops to ~25%.",
            solution: "Accept if margin stays > 20% AND it's your first Aero reference (portfolio effect). Refuse if margin falls < 15% OR if you have more lucrative parallel tenders. Rule of thumb: a contract that funds your fixed costs + delivers > 15% margin = worth signing.",
            lesson: "A counter-offer is a strong buyer-interest signal. Refusing without an alternative = guaranteed loss. Accept if the margin stays reasonable.",
          },
        ],
      },
      {
        type: 'tips',
        title: 'Pitfalls to avoid',
        items: [
          {
            title: 'Confusing desired vs. imposed duration',
            warning: "The duration shown in HST is IMPOSED by the client. No duration slider — this is deliberate and realistic: a public buyer imposes a 12- or 24-month market term, it's non-negotiable.",
          },
          {
            title: 'Underestimating bid preparation cost',
            warning: "Preparing a serious public market dossier takes 2-5 person-days (estimation, references, HSE plan, admin file). Bidding everywhere = sales team at full capacity. Better 5 well-prepared bids than 15 batch ones.",
          },
          {
            title: 'Thinking the lowest price ALWAYS wins',
            warning: "False on €100k+ markets and on all public markets. The scoring grid weights price/technical/CSR. A 10% more expensive bid with Gold EcoVadis can beat a low-cost competitor without CSR.",
          },
          {
            title: 'Ignoring the CSR ratchet effect',
            warning: "A Carrefour tender requires Silver EcoVadis minimum. If your tier isn't reached, the tender is INVISIBLE on your board — not even a chance to bid. Investing in Claire Maréchal + a CSR roadmap = an access condition, not an option.",
          },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 9. LAW & PROCEDURES (Lot 6 Phase L3 — EN mirror)
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'droit',
    title: 'Law & Procedures',
    subtitle: 'Corporate, labour, competition, international',
    icon: 'Scale',
    color: '#92400E',
    pages: [
      {
        type: 'cover',
        title: 'Law & Procedures',
        intro: "Running a transport business means navigating 6 branches of law that can summon or fine you at any time. Knowing the framework — without becoming a lawyer — helps you avoid the most expensive pitfalls and know when to pay or fight.",
        bullets: [
          'Corporate law — governance, capital, director liability',
          'Labour law — open-ended/fixed-term, dismissal, labour courts, transport CBA',
          'Competition law — cartels, dominance abuse, unfair competition',
          'Tax litigation — URSSAF / DREAL audits, tax reassessment',
          'International transport law — CMR, Incoterms, EU customs',
          'Civil and criminal liability of the director',
        ],
      },
      {
        type: 'definitions',
        title: '1. Corporate law',
        items: [
          { term: 'Share capital', def: 'Initial contribution from shareholders. EURL/SARL: €1 symbolic possible. SAS: €1 possible but €5-10k recommended for bank credibility.' },
          { term: 'Shareholders\' agreement', def: 'Confidential document complementing the bylaws and organising shareholder entry/exit, pre-emption rights, anti-dilution clauses.' },
          { term: 'Ordinary GM (AGO)', def: 'Mandatory annual meeting within 6 months after closing. Approves accounts, grants discharge to director, allocates profit.' },
          { term: 'Extraordinary GM (AGE)', def: 'Modifies bylaws (corporate purpose, capital, form). Reinforced quorum and majority. Essential to switch SARL → SAS.' },
          { term: 'Discharge of director', def: "Vote at AGO releasing director from management responsibility for the closed fiscal year. NB: does NOT cover criminal misconduct or fraud." },
          { term: 'Insolvency state', def: 'Inability to meet payable liabilities with available assets. Must be declared to court within 45 days under penalty of mismanagement.' },
        ],
      },
      {
        type: 'cases',
        title: 'Practical case — Capital increase',
        items: [
          {
            title: "Should you transform SARL into SAS to raise funds?",
            situation: "You run a SARL with €10k capital. An investor wants in for €100k against 25% equity. Your accountant suggests switching to SAS first.",
            solution: "SAS offers **total statutory flexibility** (approval clauses, controlled dilution, preferred shares). SARL imposes tax solidarity and limits agreements. **Transformation cost: ~€2,500**. For €100k raise, the investment pays back at 0.5% of the round.",
            lesson: "Above €50k of fundraising, SAS is mandatory. SARL remains relevant for family patrimonial structures without external growth ambition.",
          },
        ],
      },
      {
        type: 'definitions',
        title: '2. Labour law',
        items: [
          { term: 'CDI', def: 'Open-ended contract. Normal form of employment. Terminated by resignation, dismissal, or mutual termination agreement.' },
          { term: 'CDD', def: 'Fixed-term contract. Strictly framed use cases (replacement, temporary increase). Max 18 months including renewal.' },
          { term: 'CBA 3085', def: 'French national collective bargaining agreement for road freight transport. Guarantees minimum wages, shower/meal allowances, rest indemnities. Mandatory from the 1st driver.' },
          { term: 'Real and serious cause', def: 'Objective and verifiable motive justifying dismissal. Otherwise: minimum indemnity 3-20 months of salary depending on tenure (Macron scale).' },
          { term: 'Mutual termination', def: "Amicable agreement between employer and employee. Indemnity at least equal to legal severance. Homologation by DIRECCTE." },
          { term: 'Labour court (Prud\'hommes)', def: "Joint jurisdiction (employers + employees) for employment contract disputes. Mandatory conciliation phase before adversarial hearing." },
          { term: 'Labour inspectorate', def: "Government service that can audit your contracts, payslips, working time and safety at any moment. Power to issue formal notices and official reports (PVs)." },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Dismissal pitfalls in transport',
        items: [
          {
            title: "Vague reason (\"job mismatch\")",
            mistake: "You dismiss a driver for \"job mismatch\" without citing facts, dates, or medical incompatibilities.",
            signal: "Dismissal letter cites no dated, reproducible facts.",
            fix: "Every dismissal = circumstantial file: 3-5 dated facts (delays, documented incidents), prior written warnings, formalised pre-dismissal interview. Without these, the labour court systematically requalifies as \"without real and serious cause\" → 6+ months of salary to pay.",
          },
          {
            title: "Forgetting the CBA 3085",
            mistake: "You apply standard Labour Code minima to your HGV drivers.",
            signal: "Gross salary below conventional minimum + missing shower/meal/range allowances.",
            fix: "CBA 3085 prevails over the Labour Code. Verify at each hire: coefficient (CACES, ADR, experience), conventional monthly minimum, mandatory allowances. A single retroactive recall over 3 years costs €8-15k per affected driver.",
          },
          {
            title: "Expeditious procedure (dismissal < 7 days)",
            mistake: "Following an incident, you notify the dismissal 48 hours after the pre-dismissal interview.",
            signal: "Delay between interview and notification < 2 working days.",
            fix: "The mandatory reflection delay is **2 working days minimum** between the pre-dismissal interview and the dismissal notification (5 days for disciplinary dismissal). Any dismissal notified earlier = automatic annulment + 6 months of salary as indemnity.",
          },
        ],
      },
      {
        type: 'definitions',
        title: '3. Competition law',
        items: [
          { term: 'Illicit cartel', def: 'Agreement among competitors to fix prices, share markets, or limit output. Sanctioned up to 10% of worldwide turnover by the Competition Authority (art. L.420-1 C. com.).' },
          { term: 'Abuse of dominance', def: 'Anti-competitive conduct by a dominant company (≥ 40% market share): below-cost selling, refusal to sell, tariff discrimination (art. L.420-2 C. com.).' },
          { term: 'Unfair competition', def: 'Wrongful conduct diverting clients from a competitor: disparagement, parasitism, brand confusion, organised mass poaching. Sanctioned before commercial court.' },
          { term: 'Aggressive solicitation', def: "Limit: soliciting a competitor's clients IS allowed as long as (1) no stolen files used, (2) no false claims about the competitor, (3) no coordinated mass poaching." },
          { term: 'Non-compete clause', def: "Clause prohibiting an ex-employee from working at a competitor. Valid IF: limited in time + space + scope + financial counterpart (≥ 30% of gross salary)." },
          { term: 'Competition Authority', def: "Independent administrative authority. Referral possible by competitors, clients, or self-referral. Maximum financial sanctions: 10% of worldwide turnover." },
        ],
      },
      {
        type: 'definitions',
        title: '4. Tax litigation',
        items: [
          { term: 'Desk audit', def: 'Examination of declarations remotely by tax administration. No on-site intervention. You receive a reassessment notice without warning.' },
          { term: 'On-site audit', def: 'Inspector present in your premises for 3-6 months. Exhaustive examination of bookkeeping + invoices + declarations.' },
          { term: 'Tax reassessment', def: "Notification by administration of an error or omission. 30 days to respond (acceptance or contest). Without response: administration imposes its calculation." },
          { term: 'Hierarchical appeal', def: "First contest step: petition the supervisor. 30-day deadline. Mandatory mention at the end of reassessment notice." },
          { term: 'Litigation appeal', def: 'Petition to Administrative Court (direct taxes) or Judicial Court (VAT). Deadlines: 2 months after administration response. Specialised lawyer recommended.' },
          { term: 'Surcharges & interest', def: "Involuntary error: late interest (0.2%/month). Deliberate breach: +40%. Fraudulent manoeuvres: +80%. Penalties cumulable with criminal fines." },
        ],
      },
      {
        type: 'definitions',
        title: '5. International transport law',
        items: [
          { term: 'CMR', def: 'Convention on the Contract for the International Carriage of Goods by Road (1956). Automatically applies to any international transport where sender or recipient is in a signatory country.' },
          { term: 'CMR consignment note', def: 'Mandatory document in 3 originals (sender / recipient / carrier) materialising the contract. Without CMR: convention still applies but proof becomes complicated.' },
          { term: 'Incoterms 2020', def: '11 international commercial terms (EXW, FCA, CPT, CIP, DAP, DPU, DDP, FAS, FOB, CFR, CIF). Define who pays what, who bears risks, where transfer occurs.' },
          { term: 'EXW (Ex Works)', def: "Seller makes goods available at his factory. Buyer takes care of EVERYTHING. Minimum obligations for seller." },
          { term: 'DDP (Delivered Duty Paid)', def: "Seller delivers cleared goods at destination, duties paid. Maximum obligations for seller. Often avoided due to tax risk." },
          { term: 'T-Form (T1/T2)', def: 'EU customs transit document. T1: non-Community goods (in transit). T2: Community goods circulating. Bank guarantee required.' },
          { term: 'Intra-EU VAT reverse charge', def: 'Mechanism whereby buyer (EU taxable) liquidates VAT in his country instead of seller invoicing it. Intra-EU VAT numbers of both parties MANDATORY.' },
        ],
      },
      {
        type: 'formulas',
        title: 'CMR & Incoterms mechanics',
        items: [
          {
            name: 'CMR liability limitation',
            formula: 'Max indemnity = damaged gross weight (kg) × 8.33 SDR',
            example: "An 800 kg pallet destroyed in an accident. Max indemnity = 800 × 8.33 SDR = 6,664 SDR ≈ €8,200. Beyond: the carrier is NOT liable, save special clause or wilful misconduct.",
          },
          {
            name: 'Risk transfer (CIF Incoterm)',
            formula: 'Risks transferred at the ship\'s rail at port of shipment',
            example: "CIF sale Marseille → Mumbai. Goods damaged during crossing: the BUYER bears the loss (must trigger marine insurance). Seller released.",
          },
        ],
      },
      {
        type: 'definitions',
        title: '6. Director civil & criminal liability',
        items: [
          { term: 'Civil liability', def: 'Obligation to repair damage caused. Covered by D&O insurance — ~€150-400/month depending on size. ESSENTIAL.' },
          { term: 'Criminal liability', def: "Sanctions personal director infractions (misuse of corporate assets, undeclared work, fraud, involuntary injury). NOT covered by standard insurance." },
          { term: 'Mismanagement', def: 'Act or omission against corporate interest. Engages director liability in case of collective proceeding (action for insufficiency of assets).' },
          { term: 'Insufficiency of assets action', def: "If company is liquidated and mismanagement contributed to asset shortfall, court can order director to pay all or part of debts on personal patrimony." },
          { term: 'Obstruction offence', def: "Preventing the operation of the Works Council (CSE), refusing to convene meetings, refusing to share information. Sanction: €7,500 fine + up to 1 year imprisonment." },
          { term: 'Undeclared work', def: "Non-declaration of an employee or worked hours. €45,000 fine + 3 years prison. Systematic sanction: 5-year ban on managing + exclusion from public aid." },
        ],
      },
      {
        type: 'cases',
        title: 'Practical case — Liability after fatal accident',
        items: [
          {
            title: "Driver causes a fatal accident — what is your liability?",
            situation: "A driver causes a fatal accident following driving-time overrun (tachograph tampering). An inquiry is opened.",
            solution: "You are criminally liable if the inquiry proves: (1) you knew or should have known (no tacho audits), (2) you tacitly encouraged the overrun (impossible route targets). **Max sanction: 10 years prison + €150,000 fine + management ban + unlimited civil liability**. Your D&O does NOT cover criminal.",
            lesson: "Audit tachographs quarterly. Document every driver awareness session. A motivated refusal to take an overloaded route is better than a 10-year drama.",
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Transverse legal vocabulary',
        items: [
          { word: "Bailiff's act", def: "Official document served by a court bailiff. Indisputable proof of delivery. Starting point for procedural deadlines." },
          { word: "Writ of summons", def: "Bailiff's act summoning a party before court. Specifies jurisdiction, date, and opposing claims." },
          { word: 'Astreinte (penalty)', def: "Daily financial penalty ordered by a judge to enforce a decision. Example: €500/day late." },
          { word: 'CSE', def: "Social and Economic Committee. Mandatory from 11 employees. Staff representation, consultation on strategic directions, right of alert." },
          { word: 'DREAL', def: "Regional Directorate for Environment, Planning and Housing. Controls TRM (Road Freight Transport) in France." },
          { word: 'Formal notice', def: "Formal demand to perform an obligation within a deadline. Starting point for late interest and penalties." },
          { word: 'Suspensive appeal', def: "Appeal that suspends execution of contested decision. Rare in administrative law. Request stay explicitly." },
          { word: 'URSSAF', def: "Social contributions recovery body. Power of reassessment, recovery, account seizure." },
        ],
      },
    ],
  },

  // ════════════════════════════════════════════════════════════════════
  // 10. RISKS & CRISES
  // ════════════════════════════════════════════════════════════════════
  {
    id: 'crises',
    title: 'Risks & Crises',
    subtitle: 'Insolvency, accident, strike, plan B',
    icon: 'AlertTriangle',
    color: '#DC2626',
    pages: [
      {
        type: 'cover',
        title: 'Risks & Crises',
        intro: 'A solid transport SME does not avoid crises — it detects them early and overcomes them. Treasury, HR, accidents, strikes: let\'s see how to read the signals and structure a response.',
        bullets: [
          'Payment default vs. insolvency',
          'Strikes: impacts and levers',
          'Business continuity plan',
        ],
      },
      {
        type: 'definitions',
        title: 'Key definitions',
        items: [
          { term: 'Payment default',          def: 'Inability to meet due liabilities with available assets. Treasury < 0 + due debts → mandatory filing within 45 days.' },
          { term: 'Technical insolvency',     def: 'Equity < -10% of total assets AND negative treasury at month-end close. Immediate liquidation possible.' },
          { term: 'Insolvency streak',        def: 'Consecutive days in the danger zone (treasury < 0 AND equity < 0). At 30 days → liquidation by payment default.' },
          { term: 'EGM (extraordinary GM)',   def: 'Recapitalisation tool by partner contribution. Available once. Injects 50% of the deficit to relaunch.' },
          { term: 'National strike',          def: 'Industry-wide labour action. Revenue -50 to -100% during the duration (typical 1-3 days).' },
          { term: 'Accident',                 def: 'Material or bodily incident involving a fleet vehicle. Insurance deductible + revenue loss during repairs.' },
          { term: 'Continuity plan',          def: 'Documented procedure to maintain activity in case of major crisis (accident, strike, pandemic).' },
          { term: 'Judicial administrator',   def: 'Ministerial officer who runs the liquidation. Sells assets to repay creditors in a strict legal order.' },
        ],
      },
      {
        type: 'formulas',
        title: 'Calculation formulas',
        items: [
          {
            name: 'Treasury alert ratio',
            formula: 'Runway = Treasury ÷ Monthly fixed charges\n• > 6 months: healthy\n• 3-6 months: monitoring\n• < 3 months: red alert',
            example: 'Treasury €12,000 / Fixed charges €5,000 → 2.4 months. Immediate action required.',
          },
          {
            name: 'Accident cost',
            formula: 'Cost = deductible + (immobilisation days × lost revenue) + reputation loss',
            example: 'Deductible €1,500 + 15 d × €200/d = 1,500 + 3,000 = €4,500 + reputation impact (-3 pts).',
          },
          {
            name: 'Financial impact of a strike',
            formula: 'Loss = Average_daily_revenue × strike_days × (1 − retained_revenue_share)',
            example: 'Daily revenue €800 × 2 days × (1 - 0.5) = €800 lost (if you choose "Attempt urgent deliveries").',
          },
          {
            name: 'Minimum EGM contribution',
            formula: 'Recap = 50% × |equity deficit|',
            example: 'Equity = -€16,000 → Minimum recap = €8,000 to contribute to stop the streak.',
          },
        ],
      },
      {
        type: 'diagram',
        title: 'Timeline of a payment default',
        intro: 'There are ALWAYS 4 stages before liquidation. Each stage offers a chance to recover. Learn to recognise them early.',
        diagram: 'crisis-timeline',
        legend: [
          { label: 'Signal',  desc: 'Runway < 3 months. No due debt. First KPIs slipping.' },
          { label: 'Alert',   desc: 'Treasury < 0. Streak starts counting. Urgent bank negotiations.' },
          { label: 'Crisis',  desc: 'Streak > 14 days. EGM offered, last chance before filing.' },
          { label: 'Liq.',    desc: 'Streak >= 30 days or equity < -10% of assets. Commercial Court.' },
        ],
      },
      {
        type: 'cases',
        title: 'Case studies',
        items: [
          {
            title: 'Streak of 12 days — what to do?',
            situation: 'Your treasury has been negative for 12 consecutive days. Equity: -€8,000. You have 18 days before official payment default.',
            solution: '3-axis plan: (1) EGM for at least €4,000 (50% of deficit) = streak reset to 0. (2) Freeze hiring and postpone discretionary purchases. (3) Call the bank for a short-term credit of €10k backed by your customer receivables (Dailly assignment).',
            lesson: 'The EGM is your joker — use it when all other options have been tried, not as a first reflex.',
          },
          {
            title: '3-day national strike',
            situation: 'A transport strike is announced. You have 4 active contracts with deadlines.',
            solution: 'Option 1 "Wait": 0 revenue × 3 days, but reputation +1 (solidarity). Option 2 "Attempt urgent deliveries": 50% revenue retained × 3 days + €200 exceptional cost. Calculation: if daily revenue > €800, option 2 wins €1,000 net.',
            lesson: 'Decide based on your runway. If runway < 3 months → attempt. If runway > 6 months → respect the strike, it matters to the drivers.',
          },
          {
            title: 'Major accident (truck destroyed)',
            situation: 'Your 44t truck (value €65,000) is destroyed in an accident. Insurance covers 80%. Immobilisation forecast 3 weeks before replacement.',
            solution: 'Immediate cash: €52,000 indemnity - €1,500 deductible = €50,500. Operating loss 21 d × €300 = €6,300. Action: rent an equivalent truck €800/week × 3 = €2,400. Balance: ~€2,200 gap + 4 days of reorganisation.',
            lesson: 'ALWAYS subscribe to "operating loss" cover (~€200/mo). Covers immobilisation and avoids a €4,000-8,000 gap.',
          },
        ],
      },
      {
        type: 'pitfalls',
        title: 'Pitfalls to avoid',
        items: [
          {
            title: 'Recapitalising without changing the model',
            mistake: 'You inject €10,000 via EGM. 60 days later, you are back in the red zone.',
            signal: 'Equity turns negative again < 90 days after a recapitalisation.',
            fix: 'EGM MUST be accompanied by a structural decision: sell an under-used truck, exit a negative-margin contract, or remove a fixed charge (partial sublease of depot, for example).',
          },
          {
            title: 'Hiding the situation from suppliers',
            mistake: 'You avoid the banker and the fuel supplier to not raise alarm. 30 days later, no more fuel and a bailiff at the door.',
            signal: 'You no longer pick up the banker or accountant\'s calls.',
            fix: 'Transparency negotiated 30 days earlier saves one SME out of two. Call your banker the day you see runway dipping below 3 months.',
          },
          {
            title: 'Over-reacting to an isolated accident',
            mistake: 'One accident, you switch insurance, sell the damaged truck, fire the involved driver — with no investigation.',
            signal: 'Multiple decisions within 7 days following a single event.',
            fix: '30-day minimum delay before any structural decision post-accident. Investigate the CAUSE (driver fatigue, missed maintenance, weather). Emotional reaction costs more than the accident.',
          },
        ],
      },
      {
        type: 'vocabulary',
        title: 'Glossary',
        items: [
          { word: 'Dailly assignment',         def: 'French banking mechanism that turns customer receivables into immediate cash (with ~2-5% haircut).' },
          { word: 'Conciliation',              def: 'Amicable and confidential negotiation procedure with creditors, under a judicial conciliator.' },
          { word: 'Composition (concordat)',   def: 'Spread repayment plan negotiated with creditors, validated by the court.' },
          { word: 'Force majeure',             def: 'Unforeseeable and insurmountable event (pandemic, disaster). Suspends contractual obligations.' },
          { word: 'Formal notice',             def: 'Formal act demanding action within a deadline. Starting point of statute of limitations / penalties.' },
          { word: 'Safeguard procedure',       def: 'Preventive tool (before payment default). Gives 6 months of debt freeze to reorganise.' },
          { word: 'Judicial restructuring',    def: 'Curative procedure (after payment default). Plan over 10 years max, supervised by the court.' },
          { word: 'Commercial Court',          def: 'Court with jurisdiction over collective proceedings for businesses.' },
        ],
      },
    ],
  },
];
